import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

// @ts-nocheck
/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Thredd Platform v2 API (Internal)
 */ import { useMutation, useQuery } from "@tanstack/react-query";

import type {
  AuthForbiddenResponse,
  AuthInvalidTokenResponse,
  BinRange,
  BinRangeList,
  BinRangeSearchRequest,
  Channel,
  ChannelDirect,
  ChannelList,
  Client,
  ClientList,
  ClientUpdateRequest,
  CountryCurrencyCode,
  CountryCurrencyCodeList,
  FinancialProduct,
  FinancialProductCreateRequest,
  FinancialProductDirect,
  FinancialProductList,
  FinancialProductUpdateRequest,
  FinancialProductUpdateRequestDirect,
  InstrumentProduct,
  InstrumentProductCreateRequest,
  InstrumentProductList,
  InstrumentProductUpdateRequest,
  LedgerConfigurationDirect,
  LedgerConfigurationList,
  LedgerConfigurationSummary,
  ListChannelsDirectParams,
  ListClientBinRangesDirectParams,
  ListClientsDirectParams,
  ListFinancialProductsDirectParams,
  ListInstrumentPoliciesParams,
  ListLedgerConfigurationsDirectParams,
  ListOffsetLedgersDirectParams,
  ListPolicyGroupsDirectParams,
  ListTransactionReasonCodesDirectParams,
  MerchantCategoryCode,
  MerchantCategoryCodeList,
  OffsetLedger,
  OffsetLedgerList,
  PolicyConfigurationList,
  PolicyGroup,
  PolicyGroupList,
  ProblemDetails,
  RateLimitExceededResponse,
  RequestBadRequestResponse,
  ResourceNotFoundResponse,
  SearchInstrumentProductsDirectParams,
  ServerInternalResponse,
  TransactionReasonCode,
  TransactionReasonCodeList,
  ValidationInvalidResponse,
} from "../models";

import { restFetch } from "../../../../shell/api/auth.ts";

// https://stackoverflow.com/questions/49579094/typescript-conditional-types-filter-out-readonly-properties-pick-only-requir/49579497#49579497
type IfEquals<X, Y, A = X, B = never> =
  (<T>() => T extends X ? 1 : 2) extends <T>() => T extends Y ? 1 : 2 ? A : B;

type WritableKeys<T> = {
  [P in keyof T]-?: IfEquals<
    { [Q in P]: T[P] },
    { -readonly [Q in P]: T[P] },
    P
  >;
}[keyof T];

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;
type DistributeReadOnlyOverUnions<T> = T extends any ? NonReadonly<T> : never;

type Writable<T> = Pick<T, WritableKeys<T>>;
type NonReadonly<T> = [T] extends [UnionToIntersection<T>]
  ? {
      [P in keyof Writable<T>]: T[P] extends object
        ? NonReadonly<NonNullable<T[P]>>
        : T[P];
    }
  : DistributeReadOnlyOverUnions<T>;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type updateFinancialProductResponse200 = {
  data: FinancialProduct;
  status: 200;
};

export type updateFinancialProductResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type updateFinancialProductResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type updateFinancialProductResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type updateFinancialProductResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type updateFinancialProductResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type updateFinancialProductResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type updateFinancialProductResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type updateFinancialProductResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type updateFinancialProductResponseSuccess =
  updateFinancialProductResponse200 & {
    headers: Headers;
  };
export type updateFinancialProductResponseError = (
  | updateFinancialProductResponse400
  | updateFinancialProductResponse401
  | updateFinancialProductResponse403
  | updateFinancialProductResponse404
  | updateFinancialProductResponse409
  | updateFinancialProductResponse422
  | updateFinancialProductResponse429
  | updateFinancialProductResponse500
) & {
  headers: Headers;
};

export type updateFinancialProductResponse =
  | updateFinancialProductResponseSuccess
  | updateFinancialProductResponseError;

export const getUpdateFinancialProductUrl = (id: string) => {
  return `/api/v2/financial-products/${id}`;
};

/**
 * Updates a Financial Product in Client Lifecycle. Creates Offset Accounts and Offset Ledgers
 * in CAS and Offset Ledgers in CL if the status is LIVE.
 * @summary Updates a Financial Product. Applies additional operations based on Status.
 */
export const updateFinancialProduct = async (
  id: string,
  financialProductUpdateRequest: FinancialProductUpdateRequest,
  options?: RequestInit,
): Promise<updateFinancialProductResponse> => {
  return restFetch<updateFinancialProductResponse>(
    getUpdateFinancialProductUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(financialProductUpdateRequest),
    },
  );
};

export const getUpdateFinancialProductMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateFinancialProduct>>,
    TError,
    { id: string; data: FinancialProductUpdateRequest },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateFinancialProduct>>,
  TError,
  { id: string; data: FinancialProductUpdateRequest },
  TContext
> => {
  const mutationKey = ["updateFinancialProduct"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateFinancialProduct>>,
    { id: string; data: FinancialProductUpdateRequest }
  > = props => {
    const { id, data } = props ?? {};

    return updateFinancialProduct(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateFinancialProductMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateFinancialProduct>>
>;
export type UpdateFinancialProductMutationBody = FinancialProductUpdateRequest;
export type UpdateFinancialProductMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Updates a Financial Product. Applies additional operations based on Status.
 */
export const useUpdateFinancialProduct = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateFinancialProduct>>,
      TError,
      { id: string; data: FinancialProductUpdateRequest },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateFinancialProduct>>,
  TError,
  { id: string; data: FinancialProductUpdateRequest },
  TContext
> => {
  return useMutation(
    getUpdateFinancialProductMutationOptions(options),
    queryClient,
  );
};
export type listInstrumentPoliciesResponse200 = {
  data: PolicyConfigurationList;
  status: 200;
};

export type listInstrumentPoliciesResponse400 = {
  data: RequestBadRequestResponse;
  status: 400;
};

export type listInstrumentPoliciesResponse401 = {
  data: AuthInvalidTokenResponse;
  status: 401;
};

export type listInstrumentPoliciesResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type listInstrumentPoliciesResponse404 = {
  data: ResourceNotFoundResponse;
  status: 404;
};

export type listInstrumentPoliciesResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type listInstrumentPoliciesResponse500 = {
  data: ServerInternalResponse;
  status: 500;
};

export type listInstrumentPoliciesResponseSuccess =
  listInstrumentPoliciesResponse200 & {
    headers: Headers;
  };
export type listInstrumentPoliciesResponseError = (
  | listInstrumentPoliciesResponse400
  | listInstrumentPoliciesResponse401
  | listInstrumentPoliciesResponse403
  | listInstrumentPoliciesResponse404
  | listInstrumentPoliciesResponse429
  | listInstrumentPoliciesResponse500
) & {
  headers: Headers;
};

export type listInstrumentPoliciesResponse =
  | listInstrumentPoliciesResponseSuccess
  | listInstrumentPoliciesResponseError;

export const getListInstrumentPoliciesUrl = (
  id: string,
  params?: ListInstrumentPoliciesParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v2/instrument-products/${id}/policies?${stringifiedParams}`
    : `/api/v2/instrument-products/${id}/policies`;
};

/**
 * Retrieves instrument product policies.
 * @summary Returns multiple policies per instrument product ID
 */
export const listInstrumentPolicies = async (
  id: string,
  params?: ListInstrumentPoliciesParams,
  options?: RequestInit,
): Promise<listInstrumentPoliciesResponse> => {
  return restFetch<listInstrumentPoliciesResponse>(
    getListInstrumentPoliciesUrl(id, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListInstrumentPoliciesQueryKey = (
  id: string,
  params?: ListInstrumentPoliciesParams,
) => {
  return [
    `/api/v2/instrument-products/${id}/policies`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListInstrumentPoliciesQueryOptions = <
  TData = Awaited<ReturnType<typeof listInstrumentPolicies>>,
  TError =
    | RequestBadRequestResponse
    | AuthInvalidTokenResponse
    | AuthForbiddenResponse
    | ResourceNotFoundResponse
    | RateLimitExceededResponse
    | ServerInternalResponse,
>(
  id: string,
  params?: ListInstrumentPoliciesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listInstrumentPolicies>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListInstrumentPoliciesQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listInstrumentPolicies>>
  > = ({ signal }) =>
    listInstrumentPolicies(id, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listInstrumentPolicies>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListInstrumentPoliciesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listInstrumentPolicies>>
>;
export type ListInstrumentPoliciesQueryError =
  | RequestBadRequestResponse
  | AuthInvalidTokenResponse
  | AuthForbiddenResponse
  | ResourceNotFoundResponse
  | RateLimitExceededResponse
  | ServerInternalResponse;

export function useListInstrumentPolicies<
  TData = Awaited<ReturnType<typeof listInstrumentPolicies>>,
  TError =
    | RequestBadRequestResponse
    | AuthInvalidTokenResponse
    | AuthForbiddenResponse
    | ResourceNotFoundResponse
    | RateLimitExceededResponse
    | ServerInternalResponse,
>(
  id: string,
  params: undefined | ListInstrumentPoliciesParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listInstrumentPolicies>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listInstrumentPolicies>>,
          TError,
          Awaited<ReturnType<typeof listInstrumentPolicies>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListInstrumentPolicies<
  TData = Awaited<ReturnType<typeof listInstrumentPolicies>>,
  TError =
    | RequestBadRequestResponse
    | AuthInvalidTokenResponse
    | AuthForbiddenResponse
    | ResourceNotFoundResponse
    | RateLimitExceededResponse
    | ServerInternalResponse,
>(
  id: string,
  params?: ListInstrumentPoliciesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listInstrumentPolicies>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listInstrumentPolicies>>,
          TError,
          Awaited<ReturnType<typeof listInstrumentPolicies>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListInstrumentPolicies<
  TData = Awaited<ReturnType<typeof listInstrumentPolicies>>,
  TError =
    | RequestBadRequestResponse
    | AuthInvalidTokenResponse
    | AuthForbiddenResponse
    | ResourceNotFoundResponse
    | RateLimitExceededResponse
    | ServerInternalResponse,
>(
  id: string,
  params?: ListInstrumentPoliciesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listInstrumentPolicies>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Returns multiple policies per instrument product ID
 */

export function useListInstrumentPolicies<
  TData = Awaited<ReturnType<typeof listInstrumentPolicies>>,
  TError =
    | RequestBadRequestResponse
    | AuthInvalidTokenResponse
    | AuthForbiddenResponse
    | ResourceNotFoundResponse
    | RateLimitExceededResponse
    | ServerInternalResponse,
>(
  id: string,
  params?: ListInstrumentPoliciesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listInstrumentPolicies>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListInstrumentPoliciesQueryOptions(
    id,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type createChannelResponse201 = {
  data: Channel;
  status: 201;
};

export type createChannelResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type createChannelResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type createChannelResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type createChannelResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type createChannelResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type createChannelResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type createChannelResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type createChannelResponseSuccess = createChannelResponse201 & {
  headers: Headers;
};
export type createChannelResponseError = (
  | createChannelResponse400
  | createChannelResponse401
  | createChannelResponse403
  | createChannelResponse409
  | createChannelResponse422
  | createChannelResponse429
  | createChannelResponse500
) & {
  headers: Headers;
};

export type createChannelResponse =
  | createChannelResponseSuccess
  | createChannelResponseError;

export const getCreateChannelUrl = () => {
  return `/api/v2/channels`;
};

/**
 * Creates a channel
 * @summary Creates a channel
 */
export const createChannel = async (
  channel: Channel,
  options?: RequestInit,
): Promise<createChannelResponse> => {
  return restFetch<createChannelResponse>(getCreateChannelUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(channel),
  });
};

export const getCreateChannelMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createChannel>>,
    TError,
    { data: Channel },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createChannel>>,
  TError,
  { data: Channel },
  TContext
> => {
  const mutationKey = ["createChannel"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createChannel>>,
    { data: Channel }
  > = props => {
    const { data } = props ?? {};

    return createChannel(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateChannelMutationResult = NonNullable<
  Awaited<ReturnType<typeof createChannel>>
>;
export type CreateChannelMutationBody = Channel;
export type CreateChannelMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Creates a channel
 */
export const useCreateChannel = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createChannel>>,
      TError,
      { data: Channel },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createChannel>>,
  TError,
  { data: Channel },
  TContext
> => {
  return useMutation(getCreateChannelMutationOptions(options), queryClient);
};
export type listChannelsDirectResponse200 = {
  data: ChannelList;
  status: 200;
};

export type listChannelsDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type listChannelsDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type listChannelsDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type listChannelsDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type listChannelsDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type listChannelsDirectResponseSuccess =
  listChannelsDirectResponse200 & {
    headers: Headers;
  };
export type listChannelsDirectResponseError = (
  | listChannelsDirectResponse400
  | listChannelsDirectResponse401
  | listChannelsDirectResponse403
  | listChannelsDirectResponse429
  | listChannelsDirectResponse500
) & {
  headers: Headers;
};

export type listChannelsDirectResponse =
  | listChannelsDirectResponseSuccess
  | listChannelsDirectResponseError;

export const getListChannelsDirectUrl = (params?: ListChannelsDirectParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v2/channels?${stringifiedParams}`
    : `/api/v2/channels`;
};

/**
 * **Internal-facing endpoint** used to retrieve all or a filtered set of channels
 * on the Thredd platform.
 *
 * This endpoint provides filtering capabilities for retrieving channel records.
 * You can use one or more query parameters to filter the results.
 *
 * ## Dependencies
 *
 * None.
 *
 * ## Filtering Behaviour
 *
 * - **No query parameters**: Returns all accessible channels
 * - **Single query parameter**: Filters channels matching that specific field value
 * - **Multiple query parameters**: Returns channels matching ALL specified criteria (AND logic)
 *
 * ## Searchable Fields
 *
 * The following query parameters can be used as filters:
 * - `client_id` - Filter by owner client UUID
 * - `name` - Filter by channel name (supports partial matching)
 * - `source_type` - Filter by source type (CUSTOM)
 * - `rejection_rule` - Filter by rejection rule (REJECT_AND_REPORT, REJECT_AND_SUSPEND)
 *
 * ## Use Cases
 *
 * - List all channels for a specific client
 * - Find channels by source type
 * - Filter channels by rejection rule configuration
 * - Retrieve all channels matching specific operational criteria
 * @summary Search and list channels
 */
export const listChannelsDirect = async (
  params?: ListChannelsDirectParams,
  options?: RequestInit,
): Promise<listChannelsDirectResponse> => {
  return restFetch<listChannelsDirectResponse>(
    getListChannelsDirectUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListChannelsDirectQueryKey = (
  params?: ListChannelsDirectParams,
) => {
  return [`/api/v2/channels`, ...(params ? [params] : [])] as const;
};

export const getListChannelsDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof listChannelsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListChannelsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listChannelsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListChannelsDirectQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listChannelsDirect>>
  > = ({ signal }) => listChannelsDirect(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listChannelsDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListChannelsDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof listChannelsDirect>>
>;
export type ListChannelsDirectQueryError =
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useListChannelsDirect<
  TData = Awaited<ReturnType<typeof listChannelsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params: undefined | ListChannelsDirectParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listChannelsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listChannelsDirect>>,
          TError,
          Awaited<ReturnType<typeof listChannelsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListChannelsDirect<
  TData = Awaited<ReturnType<typeof listChannelsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListChannelsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listChannelsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listChannelsDirect>>,
          TError,
          Awaited<ReturnType<typeof listChannelsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListChannelsDirect<
  TData = Awaited<ReturnType<typeof listChannelsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListChannelsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listChannelsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Search and list channels
 */

export function useListChannelsDirect<
  TData = Awaited<ReturnType<typeof listChannelsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListChannelsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listChannelsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListChannelsDirectQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type listClientsDirectResponse200 = {
  data: ClientList;
  status: 200;
};

export type listClientsDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type listClientsDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type listClientsDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type listClientsDirectResponse422 = {
  data: ProblemDetails;
  status: 422;
};

export type listClientsDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type listClientsDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type listClientsDirectResponseSuccess = listClientsDirectResponse200 & {
  headers: Headers;
};
export type listClientsDirectResponseError = (
  | listClientsDirectResponse400
  | listClientsDirectResponse401
  | listClientsDirectResponse403
  | listClientsDirectResponse422
  | listClientsDirectResponse429
  | listClientsDirectResponse500
) & {
  headers: Headers;
};

export type listClientsDirectResponse =
  | listClientsDirectResponseSuccess
  | listClientsDirectResponseError;

export const getListClientsDirectUrl = (params?: ListClientsDirectParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v2/clients?${stringifiedParams}`
    : `/api/v2/clients`;
};

/**
 * **Internal-facing endpoint** used to retrieve all or filtered clients onboarded on the Thredd platform.
 *
 * This endpoint provides flexible search and filtering capabilities for retrieving client records.
 * You can use one or more query parameters as search criteria to filter the results.
 *
 * ## Dependencies
 *
 * None.
 *
 * ## Filtering Behaviour
 *
 * - **No query parameters**: Returns all accessible clients
 * - **Single field**: Filters clients matching that specific field value
 * - **Multiple fields**: Returns clients matching ALL specified criteria (AND logic)
 *
 * ## Searchable Fields
 *
 * All client fields can be used as search criteria, including:
 * - `id` - Exact UUID match
 * - `parentId` - Find sub-clients of a specific MSA client
 * - `type` - Filter by MSA or SUB clients
 * - `name` - Filter by legal registered name (supports partial matching)
 * - `status` - Filter by operational status (ACTIVE, OPEN, CLOSED, DRAFT, CLOSURE_SCHEDULED)
 * - `reportingCurrency` - Filter by reporting currency
 * - `reportingCutoffTime` - Filter by reporting cutoff time
 * - `reportingTimezone` - Filter by reporting timezone
 * - `rulesType` - Filter by rule set type (GLOBAL, LOCAL)
 * - `brandName` - Filter by marketing/branding name
 * - `issuerName` - Filter by issuer name
 * - `issuerCode` - Filter by issuer code
 * - `contactName` - Filter by contact name
 * - `programManagerCode` - APEX Project Manager Code
 * - `programManagerId` - APEX Project Manager id
 *
 * ## Use Cases
 *
 * - List all clients in the system
 * - Find all sub-clients under a specific MSA client
 * - Filter clients by operational status
 * - Search for clients by name or reference
 * - Find clients using a specific reporting currency
 * - Retrieve clients matching specific configuration criteria
 * @summary Search and list clients
 */
export const listClientsDirect = async (
  params?: ListClientsDirectParams,
  options?: RequestInit,
): Promise<listClientsDirectResponse> => {
  return restFetch<listClientsDirectResponse>(getListClientsDirectUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListClientsDirectQueryKey = (
  params?: ListClientsDirectParams,
) => {
  return [`/api/v2/clients`, ...(params ? [params] : [])] as const;
};

export const getListClientsDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof listClientsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListClientsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listClientsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListClientsDirectQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listClientsDirect>>
  > = ({ signal }) => listClientsDirect(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listClientsDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListClientsDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof listClientsDirect>>
>;
export type ListClientsDirectQueryError =
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useListClientsDirect<
  TData = Awaited<ReturnType<typeof listClientsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params: undefined | ListClientsDirectParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listClientsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listClientsDirect>>,
          TError,
          Awaited<ReturnType<typeof listClientsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListClientsDirect<
  TData = Awaited<ReturnType<typeof listClientsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListClientsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listClientsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listClientsDirect>>,
          TError,
          Awaited<ReturnType<typeof listClientsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListClientsDirect<
  TData = Awaited<ReturnType<typeof listClientsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListClientsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listClientsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Search and list clients
 */

export function useListClientsDirect<
  TData = Awaited<ReturnType<typeof listClientsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListClientsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listClientsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListClientsDirectQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type createClientDirectResponse201 = {
  data: Client;
  status: 201;
};

export type createClientDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type createClientDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type createClientDirectResponse403 = {
  data: ProblemDetails;
  status: 403;
};

export type createClientDirectResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type createClientDirectResponse422 = {
  data: ProblemDetails;
  status: 422;
};

export type createClientDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type createClientDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type createClientDirectResponseSuccess =
  createClientDirectResponse201 & {
    headers: Headers;
  };
export type createClientDirectResponseError = (
  | createClientDirectResponse400
  | createClientDirectResponse401
  | createClientDirectResponse403
  | createClientDirectResponse409
  | createClientDirectResponse422
  | createClientDirectResponse429
  | createClientDirectResponse500
) & {
  headers: Headers;
};

export type createClientDirectResponse =
  | createClientDirectResponseSuccess
  | createClientDirectResponseError;

export const getCreateClientDirectUrl = () => {
  return `/api/v2/clients`;
};

/**
 * **Internal-facing endpoint** used to create a new client using information collected during onboarding.
 *
 * Creates a new client in the Thredd platform. A client represents a business entity that can have multiple
 * programmes, financial products, and instrument products associated with it.
 *
 * ## Client Types
 *
 * - **MSA (Master Service Agreement) Client**: A client with a direct contractual arrangement with Thredd.
 *   MSA clients can have sub-clients and define the base configuration.
 * - **SUB (Sub-client)**: A client operating under an MSA client. Sub-clients inherit configurations from
 *   the MSA or define their own operational parameters. For sub-clients, some fields are pre-configured
 *   or not available for customisation.
 *
 * ## Dependencies
 *
 * - **parentId**: Required for sub-clients. A sub-client must be associated with a parent MSA client.
 *
 * ## Conditional Requirements
 *
 * ### Always required:
 * - `type` - MSA or SUB
 * - `name` - Legal registered name
 * - `status` - Operational status
 *
 * ### Required for SUB clients:
 * - `parentId` - UUID of the parent MSA client
 * - `category` - DEBIT or CREDIT
 *
 * ### Optional for all clients:
 * - `contactName`, `addresses`, `emails`, `phoneNumbers`
 * - `reportingCurrency` - defaults to USD (inherited from the parent for SUB)
 * - `brandName`, `issuerName`, `issuerCode`
 *
 * ### Optional for SUB clients:
 * - `reportingCutoffTime` - defaults to 00:00:00
 * - `reportingTimezone` - defaults to UTC
 *
 * ### Server-assigned:
 * - `rulesType` - set to GLOBAL for MSA; inherited from the parent for SUB
 *   unless explicitly supplied
 * - `referenceNumber`, `id`, `createdAt`, `createdBy`
 *
 * ### Prohibited for MSA clients:
 * - `parentId`, `category`, `institutionName`, `institutionId`,
 *   `reportingCutoffTime`, `reportingTimezone`
 *
 * ## Client Status
 *
 * A client submitted with any status other than `DRAFT` is created as `ACTIVE`
 * once it passes validation — no secondary update call is needed to activate it.
 * `DRAFT` clients stay in `DRAFT`, and progressing one to `OPEN` activates it in
 * the same way.
 *
 * Closing an `ACTIVE` client takes two steps: `CLOSURE_SCHEDULED`, then `CLOSED`.
 * @summary Create a new client
 */
export const createClientDirect = async (
  client: NonReadonly<Client>,
  options?: RequestInit,
): Promise<createClientDirectResponse> => {
  return restFetch<createClientDirectResponse>(getCreateClientDirectUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(client),
  });
};

export const getCreateClientDirectMutationOptions = <
  TError = ProblemDetails | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createClientDirect>>,
    TError,
    { data: NonReadonly<Client> },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createClientDirect>>,
  TError,
  { data: NonReadonly<Client> },
  TContext
> => {
  const mutationKey = ["createClientDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createClientDirect>>,
    { data: NonReadonly<Client> }
  > = props => {
    const { data } = props ?? {};

    return createClientDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateClientDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof createClientDirect>>
>;
export type CreateClientDirectMutationBody = NonReadonly<Client>;
export type CreateClientDirectMutationError =
  | ProblemDetails
  | RateLimitExceededResponse;

/**
 * @summary Create a new client
 */
export const useCreateClientDirect = <
  TError = ProblemDetails | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createClientDirect>>,
      TError,
      { data: NonReadonly<Client> },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createClientDirect>>,
  TError,
  { data: NonReadonly<Client> },
  TContext
> => {
  return useMutation(
    getCreateClientDirectMutationOptions(options),
    queryClient,
  );
};
export type getClientDirectResponse200 = {
  data: Client;
  status: 200;
};

export type getClientDirectResponse400 = {
  data: RequestBadRequestResponse;
  status: 400;
};

export type getClientDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type getClientDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type getClientDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type getClientDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type getClientDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type getClientDirectResponseSuccess = getClientDirectResponse200 & {
  headers: Headers;
};
export type getClientDirectResponseError = (
  | getClientDirectResponse400
  | getClientDirectResponse401
  | getClientDirectResponse403
  | getClientDirectResponse404
  | getClientDirectResponse429
  | getClientDirectResponse500
) & {
  headers: Headers;
};

export type getClientDirectResponse =
  | getClientDirectResponseSuccess
  | getClientDirectResponseError;

export const getGetClientDirectUrl = (clientId: string) => {
  return `/api/v2/clients/${clientId}`;
};

/**
 * **Internal-facing endpoint** used to retrieve a client onboarded on the Thredd platform.
 *
 * Retrieves detailed information about a specific client using its unique identifier (UUID).
 * Returns all client attributes including:
 * - Client identification and hierarchy (parent relationships)
 * - Operational status and configuration
 * - Reporting settings (currency, cutoff time, timezone)
 * - Contact information (addresses, emails, phone numbers)
 * - Audit trail (creation and update timestamps, user tracking)
 * - Version information for optimistic locking
 *
 * ## Dependencies
 *
 * None.
 *
 * ## Use Cases
 *
 * - View complete client profile and configuration
 * - Verify client onboarding status
 * - Retrieve client information for administrative purposes
 * - Audit client data and relationships
 * @summary Retrieve a single client by ID
 */
export const getClientDirect = async (
  clientId: string,
  options?: RequestInit,
): Promise<getClientDirectResponse> => {
  return restFetch<getClientDirectResponse>(getGetClientDirectUrl(clientId), {
    ...options,
    method: "GET",
  });
};

export const getGetClientDirectQueryKey = (clientId: string) => {
  return [`/api/v2/clients/${clientId}`] as const;
};

export const getGetClientDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof getClientDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getClientDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetClientDirectQueryKey(clientId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getClientDirect>>> = ({
    signal,
  }) => getClientDirect(clientId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: clientId !== null && clientId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getClientDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof getClientDirect>>
>;
export type GetClientDirectQueryError =
  | RequestBadRequestResponse
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useGetClientDirect<
  TData = Awaited<ReturnType<typeof getClientDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getClientDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getClientDirect>>,
          TError,
          Awaited<ReturnType<typeof getClientDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetClientDirect<
  TData = Awaited<ReturnType<typeof getClientDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getClientDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getClientDirect>>,
          TError,
          Awaited<ReturnType<typeof getClientDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetClientDirect<
  TData = Awaited<ReturnType<typeof getClientDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getClientDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Retrieve a single client by ID
 */

export function useGetClientDirect<
  TData = Awaited<ReturnType<typeof getClientDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getClientDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetClientDirectQueryOptions(clientId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updateClientDirectResponse200 = {
  data: Client;
  status: 200;
};

export type updateClientDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type updateClientDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type updateClientDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type updateClientDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type updateClientDirectResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type updateClientDirectResponse422 = {
  data: ProblemDetails;
  status: 422;
};

export type updateClientDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type updateClientDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type updateClientDirectResponseSuccess =
  updateClientDirectResponse200 & {
    headers: Headers;
  };
export type updateClientDirectResponseError = (
  | updateClientDirectResponse400
  | updateClientDirectResponse401
  | updateClientDirectResponse403
  | updateClientDirectResponse404
  | updateClientDirectResponse409
  | updateClientDirectResponse422
  | updateClientDirectResponse429
  | updateClientDirectResponse500
) & {
  headers: Headers;
};

export type updateClientDirectResponse =
  | updateClientDirectResponseSuccess
  | updateClientDirectResponseError;

export const getUpdateClientDirectUrl = (clientId: string) => {
  return `/api/v2/clients/${clientId}`;
};

/**
 * **Internal-facing endpoint** used to update the information for an existing client.
 *
 * Updates an existing client's information using partial update semantics. Only the fields
 * provided in the request body will be updated - all fields are optional in the update request.
 *
 * ## Dependencies
 *
 * None.
 *
 * ## Updatable Fields
 *
 * The following fields can be updated:
 * - `name` - Legal registered name
 * - `status` - Operational status (ACTIVE, OPEN, CLOSED, DRAFT, CLOSURE_SCHEDULED)
 * - `reportingCurrency` - ISO 4217 currency code
 * - `reportingCutoffTime` - ISO 8601 time
 * - `reportingTimezone` - IANA timezone
 * - `brandName` - Marketing/branding name
 * - `issuerCode` - Issuer identification code
 * - `issuerName` - Issuer institution name
 * - `contactName` - Primary contact person
 * - `addresses` - Contact addresses
 * - `emails` - Contact emails
 * - `phoneNumbers` - Contact phone numbers
 * - `programManagerCode` - APEX Project Manager Code
 * - `programManagerId` - APEX Project Manager id
 *
 * ## Immutable Fields
 *
 * The following fields cannot be updated after creation:
 * - `id` - System-generated UUID
 * - `parentId` - Parent client relationship (set at creation)
 * - `referenceNumber` - System-generated reference
 * - `type` - Client type (MSA or SUB)
 * - `rulesType` - Rule set type (GLOBAL or LOCAL)
 * - `category` - DEBIT or CREDIT (set at creation)
 *
 * ## Optimistic Locking
 *
 * The `version` field is used for optimistic locking. If the version in the database
 * differs from the expected version, a 409 Conflict error is returned, indicating
 * the client was modified by another process.
 *
 * ## Audit Trail
 *
 * The system automatically updates:
 * - `updatedAt` - Timestamp of the update
 * - `updatedBy` - User who performed the update
 * - `version` - Incremented for optimistic locking
 * @summary Update an existing client
 */
export const updateClientDirect = async (
  clientId: string,
  clientUpdateRequest: ClientUpdateRequest,
  options?: RequestInit,
): Promise<updateClientDirectResponse> => {
  return restFetch<updateClientDirectResponse>(
    getUpdateClientDirectUrl(clientId),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(clientUpdateRequest),
    },
  );
};

export const getUpdateClientDirectMutationOptions = <
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateClientDirect>>,
    TError,
    { clientId: string; data: ClientUpdateRequest },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateClientDirect>>,
  TError,
  { clientId: string; data: ClientUpdateRequest },
  TContext
> => {
  const mutationKey = ["updateClientDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateClientDirect>>,
    { clientId: string; data: ClientUpdateRequest }
  > = props => {
    const { clientId, data } = props ?? {};

    return updateClientDirect(clientId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateClientDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateClientDirect>>
>;
export type UpdateClientDirectMutationBody = ClientUpdateRequest;
export type UpdateClientDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

/**
 * @summary Update an existing client
 */
export const useUpdateClientDirect = <
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateClientDirect>>,
      TError,
      { clientId: string; data: ClientUpdateRequest },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateClientDirect>>,
  TError,
  { clientId: string; data: ClientUpdateRequest },
  TContext
> => {
  return useMutation(
    getUpdateClientDirectMutationOptions(options),
    queryClient,
  );
};
export type createFinancialProductDirectResponse201 = {
  data: FinancialProductDirect;
  status: 201;
};

export type createFinancialProductDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type createFinancialProductDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type createFinancialProductDirectResponse403 = {
  data: ProblemDetails;
  status: 403;
};

export type createFinancialProductDirectResponse404 = {
  data: ResourceNotFoundResponse;
  status: 404;
};

export type createFinancialProductDirectResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type createFinancialProductDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type createFinancialProductDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type createFinancialProductDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type createFinancialProductDirectResponseSuccess =
  createFinancialProductDirectResponse201 & {
    headers: Headers;
  };
export type createFinancialProductDirectResponseError = (
  | createFinancialProductDirectResponse400
  | createFinancialProductDirectResponse401
  | createFinancialProductDirectResponse403
  | createFinancialProductDirectResponse404
  | createFinancialProductDirectResponse409
  | createFinancialProductDirectResponse422
  | createFinancialProductDirectResponse429
  | createFinancialProductDirectResponse500
) & {
  headers: Headers;
};

export type createFinancialProductDirectResponse =
  | createFinancialProductDirectResponseSuccess
  | createFinancialProductDirectResponseError;

export const getCreateFinancialProductDirectUrl = (clientId: string) => {
  return `/api/v2/clients/${clientId}/financial-products`;
};

/**
 * **Internal-facing endpoint** used to create a new financial product based on data collected during onboarding.
 *
 * A financial product can be owned by a client. The product's configuration is closely related to the
 * type of client owner:
 * - **MSA Client**: Can create GLOBAL scoped products, which related sub-clients can access
 * - **Sub-client**: Can create only LOCAL scoped products, restricted to the owner only
 *
 * ## Dependencies
 *
 * - **clientId** path parameter: A created client (MSA client for GLOBAL products, sub-client for LOCAL products)
 *
 * ## Restrictions
 *
 * - MSA client required for GLOBAL scoped products
 * - Sub-client required for LOCAL scoped products
 *
 * ## Conditional Requirements
 *
 * ### Always Required:
 * - `clientId` path parameter - UUID of the client that owns the product
 * - `name` - Name of the financial product
 * - `status` - Operational status
 *
 * ### Required for non-DRAFT status:
 * - `category` - Product category (DEBIT or CREDIT)
 * - `scope` - Product scope (GLOBAL or LOCAL)
 *
 * ### Optional:
 * - `referenceId` - Customer-friendly unique reference identifier
 * - `description` - Description of the product
 * - `activeStart` - Start date when product becomes active
 * - `activeEnd` - End date when product becomes inactive
 * - `subClientIds` - List of sub-client IDs (GLOBAL scope only)
 * @summary Create a new financial product
 */
export const createFinancialProductDirect = async (
  clientId: string,
  financialProductCreateRequest: FinancialProductCreateRequest,
  options?: RequestInit,
): Promise<createFinancialProductDirectResponse> => {
  return restFetch<createFinancialProductDirectResponse>(
    getCreateFinancialProductDirectUrl(clientId),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(financialProductCreateRequest),
    },
  );
};

export const getCreateFinancialProductDirectMutationOptions = <
  TError =
    | ProblemDetails
    | ResourceNotFoundResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createFinancialProductDirect>>,
    TError,
    { clientId: string; data: FinancialProductCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createFinancialProductDirect>>,
  TError,
  { clientId: string; data: FinancialProductCreateRequest },
  TContext
> => {
  const mutationKey = ["createFinancialProductDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createFinancialProductDirect>>,
    { clientId: string; data: FinancialProductCreateRequest }
  > = props => {
    const { clientId, data } = props ?? {};

    return createFinancialProductDirect(clientId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateFinancialProductDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof createFinancialProductDirect>>
>;
export type CreateFinancialProductDirectMutationBody =
  FinancialProductCreateRequest;
export type CreateFinancialProductDirectMutationError =
  | ProblemDetails
  | ResourceNotFoundResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Create a new financial product
 */
export const useCreateFinancialProductDirect = <
  TError =
    | ProblemDetails
    | ResourceNotFoundResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createFinancialProductDirect>>,
      TError,
      { clientId: string; data: FinancialProductCreateRequest },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createFinancialProductDirect>>,
  TError,
  { clientId: string; data: FinancialProductCreateRequest },
  TContext
> => {
  return useMutation(
    getCreateFinancialProductDirectMutationOptions(options),
    queryClient,
  );
};
export type getFinancialProductByIdDirectResponse200 = {
  data: FinancialProductDirect;
  status: 200;
};

export type getFinancialProductByIdDirectResponse400 = {
  data: RequestBadRequestResponse;
  status: 400;
};

export type getFinancialProductByIdDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type getFinancialProductByIdDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type getFinancialProductByIdDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type getFinancialProductByIdDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type getFinancialProductByIdDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type getFinancialProductByIdDirectResponseSuccess =
  getFinancialProductByIdDirectResponse200 & {
    headers: Headers;
  };
export type getFinancialProductByIdDirectResponseError = (
  | getFinancialProductByIdDirectResponse400
  | getFinancialProductByIdDirectResponse401
  | getFinancialProductByIdDirectResponse403
  | getFinancialProductByIdDirectResponse404
  | getFinancialProductByIdDirectResponse429
  | getFinancialProductByIdDirectResponse500
) & {
  headers: Headers;
};

export type getFinancialProductByIdDirectResponse =
  | getFinancialProductByIdDirectResponseSuccess
  | getFinancialProductByIdDirectResponseError;

export const getGetFinancialProductByIdDirectUrl = (
  clientId: string,
  id: string,
) => {
  return `/api/v2/clients/${clientId}/financial-products/${id}`;
};

/**
 * **Internal-facing endpoint** used to retrieve a financial product created on the Thredd platform.
 *
 * Retrieves detailed information about a specific financial product using its unique identifier (UUID).
 * Returns all product attributes including:
 * - Product identification and ownership
 * - Scope and category configuration
 * - Operational status
 * - Activation dates and validity period
 * - Associated sub-clients (for GLOBAL products)
 * - Audit trail (creation and update timestamps, user tracking)
 * - Version information for optimistic locking
 *
 * ## Dependencies
 *
 * - The ID of a previously created financial product
 *
 * ## Use Cases
 *
 * - View complete financial product configuration
 * - Verify product onboarding status
 * - Retrieve product information for operational purposes
 * - Audit product data and relationships
 * @summary Retrieve a single financial product by ID
 */
export const getFinancialProductByIdDirect = async (
  clientId: string,
  id: string,
  options?: RequestInit,
): Promise<getFinancialProductByIdDirectResponse> => {
  return restFetch<getFinancialProductByIdDirectResponse>(
    getGetFinancialProductByIdDirectUrl(clientId, id),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetFinancialProductByIdDirectQueryKey = (
  clientId: string,
  id: string,
) => {
  return [`/api/v2/clients/${clientId}/financial-products/${id}`] as const;
};

export const getGetFinancialProductByIdDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFinancialProductByIdDirectQueryKey(clientId, id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFinancialProductByIdDirect>>
  > = ({ signal }) =>
    getFinancialProductByIdDirect(clientId, id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled:
      clientId !== null &&
      clientId !== undefined &&
      id !== null &&
      id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetFinancialProductByIdDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFinancialProductByIdDirect>>
>;
export type GetFinancialProductByIdDirectQueryError =
  | RequestBadRequestResponse
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useGetFinancialProductByIdDirect<
  TData = Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
          TError,
          Awaited<ReturnType<typeof getFinancialProductByIdDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetFinancialProductByIdDirect<
  TData = Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
          TError,
          Awaited<ReturnType<typeof getFinancialProductByIdDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetFinancialProductByIdDirect<
  TData = Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Retrieve a single financial product by ID
 */

export function useGetFinancialProductByIdDirect<
  TData = Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFinancialProductByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetFinancialProductByIdDirectQueryOptions(
    clientId,
    id,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updateFinancialProductDirectResponse200 = {
  data: FinancialProductDirect;
  status: 200;
};

export type updateFinancialProductDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type updateFinancialProductDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type updateFinancialProductDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type updateFinancialProductDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type updateFinancialProductDirectResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type updateFinancialProductDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type updateFinancialProductDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type updateFinancialProductDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type updateFinancialProductDirectResponseSuccess =
  updateFinancialProductDirectResponse200 & {
    headers: Headers;
  };
export type updateFinancialProductDirectResponseError = (
  | updateFinancialProductDirectResponse400
  | updateFinancialProductDirectResponse401
  | updateFinancialProductDirectResponse403
  | updateFinancialProductDirectResponse404
  | updateFinancialProductDirectResponse409
  | updateFinancialProductDirectResponse422
  | updateFinancialProductDirectResponse429
  | updateFinancialProductDirectResponse500
) & {
  headers: Headers;
};

export type updateFinancialProductDirectResponse =
  | updateFinancialProductDirectResponseSuccess
  | updateFinancialProductDirectResponseError;

export const getUpdateFinancialProductDirectUrl = (
  clientId: string,
  id: string,
) => {
  return `/api/v2/clients/${clientId}/financial-products/${id}`;
};

/**
 * **Internal-facing endpoint** used to update the information for an existing financial product.
 *
 * Updates an existing financial product's information using partial update semantics.
 * Only the fields provided in the request body will be updated - all fields are optional in the update request.
 *
 * ## Dependencies
 *
 * - **id**: An existing financial product
 *
 * ## Immutable Fields
 *
 * The following fields cannot be updated after creation:
 * - `id` - System-generated UUID
 * - `clientId` - Owner client cannot be changed
 * - `scope` - Scope type cannot be modified (GLOBAL/LOCAL)
 *
 * ## Updatable Fields
 *
 * The following fields can be updated:
 * - `referenceId` - Customer-friendly reference identifier
 * - `name` - Name of the financial product
 * - `status` - Operational status (PROCESSING, OPEN, CLOSED, DRAFT, AWAITING_APPROVAL, APPROVED, LIVE, SUSPENDED)
 * - `description` - Description of the product
 * - `activeStart` - Start date when product becomes active
 * - `activeEnd` - End date when product becomes inactive
 * - `subClientIds` - List of sub-client IDs (GLOBAL scope only)
 *
 * ## Optimistic Locking
 *
 * The `version` field is used for optimistic locking. If the version in the database
 * differs from the expected version, a 409 Conflict error is returned, indicating
 * the financial product was modified by another process.
 *
 * ## Audit Trail
 *
 * The system automatically updates:
 * - `updatedAt` - Timestamp of the update
 * - `updatedBy` - User who performed the update
 * - `version` - Incremented for optimistic locking
 * @summary Update an existing financial product
 */
export const updateFinancialProductDirect = async (
  clientId: string,
  id: string,
  financialProductUpdateRequestDirect: FinancialProductUpdateRequestDirect,
  options?: RequestInit,
): Promise<updateFinancialProductDirectResponse> => {
  return restFetch<updateFinancialProductDirectResponse>(
    getUpdateFinancialProductDirectUrl(clientId, id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(financialProductUpdateRequestDirect),
    },
  );
};

export const getUpdateFinancialProductDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateFinancialProductDirect>>,
    TError,
    { clientId: string; id: string; data: FinancialProductUpdateRequestDirect },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateFinancialProductDirect>>,
  TError,
  { clientId: string; id: string; data: FinancialProductUpdateRequestDirect },
  TContext
> => {
  const mutationKey = ["updateFinancialProductDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateFinancialProductDirect>>,
    { clientId: string; id: string; data: FinancialProductUpdateRequestDirect }
  > = props => {
    const { clientId, id, data } = props ?? {};

    return updateFinancialProductDirect(clientId, id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateFinancialProductDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateFinancialProductDirect>>
>;
export type UpdateFinancialProductDirectMutationBody =
  FinancialProductUpdateRequestDirect;
export type UpdateFinancialProductDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Update an existing financial product
 */
export const useUpdateFinancialProductDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateFinancialProductDirect>>,
      TError,
      {
        clientId: string;
        id: string;
        data: FinancialProductUpdateRequestDirect;
      },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateFinancialProductDirect>>,
  TError,
  { clientId: string; id: string; data: FinancialProductUpdateRequestDirect },
  TContext
> => {
  return useMutation(
    getUpdateFinancialProductDirectMutationOptions(options),
    queryClient,
  );
};
export type listFinancialProductsDirectResponse200 = {
  data: FinancialProductList;
  status: 200;
};

export type listFinancialProductsDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type listFinancialProductsDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type listFinancialProductsDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type listFinancialProductsDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type listFinancialProductsDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type listFinancialProductsDirectResponseSuccess =
  listFinancialProductsDirectResponse200 & {
    headers: Headers;
  };
export type listFinancialProductsDirectResponseError = (
  | listFinancialProductsDirectResponse400
  | listFinancialProductsDirectResponse401
  | listFinancialProductsDirectResponse403
  | listFinancialProductsDirectResponse429
  | listFinancialProductsDirectResponse500
) & {
  headers: Headers;
};

export type listFinancialProductsDirectResponse =
  | listFinancialProductsDirectResponseSuccess
  | listFinancialProductsDirectResponseError;

export const getListFinancialProductsDirectUrl = (
  params?: ListFinancialProductsDirectParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v2/financial-products?${stringifiedParams}`
    : `/api/v2/financial-products`;
};

/**
 * **Internal-facing endpoint** used to retrieve all or a filtered set of financial products on the Thredd platform.
 *
 * This endpoint provides filtering capabilities for retrieving financial product records.
 * You can use one or more query parameters to filter the results.
 *
 * ## Dependencies
 *
 * None.
 *
 * ## Filtering Behaviour
 *
 * - **No query parameters**: Returns all accessible financial products
 * - **Single query parameter**: Filters products matching that specific field value
 * - **Multiple query parameters**: Returns products matching ALL specified criteria (AND logic)
 *
 * ## Searchable Fields
 *
 * The following query parameters can be used as filters:
 * - `id` - Exact UUID match
 * - `clientId` - Filter by owner client
 * - `referenceId` - Filter by customer-friendly reference
 * - `category` - Filter by DEBIT or CREDIT
 * - `name` - Filter by product name (supports partial matching)
 * - `scope` - Filter by GLOBAL or LOCAL scope
 * - `status` - Filter by operational status (PROCESSING, OPEN, CLOSED, DRAFT, AWAITING_APPROVAL, APPROVED, LIVE, SUSPENDED)
 * - `activeStart` - Filter by activation start date
 * - `activeEnd` - Filter by activation end date
 *
 * ## Use Cases
 *
 * - List all financial products in the system
 * - Find all products owned by a specific client
 * - Filter products by scope (GLOBAL vs LOCAL)
 * - Search for products by category or status
 * - Retrieve products matching specific activation dates
 * - Find products by reference identifier
 * @summary Search and list financial products
 */
export const listFinancialProductsDirect = async (
  params?: ListFinancialProductsDirectParams,
  options?: RequestInit,
): Promise<listFinancialProductsDirectResponse> => {
  return restFetch<listFinancialProductsDirectResponse>(
    getListFinancialProductsDirectUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListFinancialProductsDirectQueryKey = (
  params?: ListFinancialProductsDirectParams,
) => {
  return [`/api/v2/financial-products`, ...(params ? [params] : [])] as const;
};

export const getListFinancialProductsDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof listFinancialProductsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListFinancialProductsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listFinancialProductsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListFinancialProductsDirectQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listFinancialProductsDirect>>
  > = ({ signal }) =>
    listFinancialProductsDirect(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listFinancialProductsDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListFinancialProductsDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof listFinancialProductsDirect>>
>;
export type ListFinancialProductsDirectQueryError =
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useListFinancialProductsDirect<
  TData = Awaited<ReturnType<typeof listFinancialProductsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params: undefined | ListFinancialProductsDirectParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listFinancialProductsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFinancialProductsDirect>>,
          TError,
          Awaited<ReturnType<typeof listFinancialProductsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListFinancialProductsDirect<
  TData = Awaited<ReturnType<typeof listFinancialProductsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListFinancialProductsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listFinancialProductsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFinancialProductsDirect>>,
          TError,
          Awaited<ReturnType<typeof listFinancialProductsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListFinancialProductsDirect<
  TData = Awaited<ReturnType<typeof listFinancialProductsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListFinancialProductsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listFinancialProductsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Search and list financial products
 */

export function useListFinancialProductsDirect<
  TData = Awaited<ReturnType<typeof listFinancialProductsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListFinancialProductsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listFinancialProductsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListFinancialProductsDirectQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type searchInstrumentProductsDirectResponse200 = {
  data: InstrumentProductList;
  status: 200;
};

export type searchInstrumentProductsDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type searchInstrumentProductsDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type searchInstrumentProductsDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type searchInstrumentProductsDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type searchInstrumentProductsDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type searchInstrumentProductsDirectResponseSuccess =
  searchInstrumentProductsDirectResponse200 & {
    headers: Headers;
  };
export type searchInstrumentProductsDirectResponseError = (
  | searchInstrumentProductsDirectResponse400
  | searchInstrumentProductsDirectResponse401
  | searchInstrumentProductsDirectResponse403
  | searchInstrumentProductsDirectResponse429
  | searchInstrumentProductsDirectResponse500
) & {
  headers: Headers;
};

export type searchInstrumentProductsDirectResponse =
  | searchInstrumentProductsDirectResponseSuccess
  | searchInstrumentProductsDirectResponseError;

export const getSearchInstrumentProductsDirectUrl = (
  params?: SearchInstrumentProductsDirectParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v2/instrument-products?${stringifiedParams}`
    : `/api/v2/instrument-products`;
};

/**
 * View multiple instrument products
 * @summary View multiple instrument products
 */
export const searchInstrumentProductsDirect = async (
  params?: SearchInstrumentProductsDirectParams,
  options?: RequestInit,
): Promise<searchInstrumentProductsDirectResponse> => {
  return restFetch<searchInstrumentProductsDirectResponse>(
    getSearchInstrumentProductsDirectUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getSearchInstrumentProductsDirectQueryKey = (
  params?: SearchInstrumentProductsDirectParams,
) => {
  return [`/api/v2/instrument-products`, ...(params ? [params] : [])] as const;
};

export const getSearchInstrumentProductsDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: SearchInstrumentProductsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSearchInstrumentProductsDirectQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof searchInstrumentProductsDirect>>
  > = ({ signal }) =>
    searchInstrumentProductsDirect(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchInstrumentProductsDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof searchInstrumentProductsDirect>>
>;
export type SearchInstrumentProductsDirectQueryError =
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useSearchInstrumentProductsDirect<
  TData = Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params: undefined | SearchInstrumentProductsDirectParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
          TError,
          Awaited<ReturnType<typeof searchInstrumentProductsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSearchInstrumentProductsDirect<
  TData = Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: SearchInstrumentProductsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
          TError,
          Awaited<ReturnType<typeof searchInstrumentProductsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSearchInstrumentProductsDirect<
  TData = Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: SearchInstrumentProductsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary View multiple instrument products
 */

export function useSearchInstrumentProductsDirect<
  TData = Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: SearchInstrumentProductsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof searchInstrumentProductsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getSearchInstrumentProductsDirectQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type createInstrumentProductDirectResponse201 = {
  data: InstrumentProduct;
  status: 201;
};

export type createInstrumentProductDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type createInstrumentProductDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type createInstrumentProductDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type createInstrumentProductDirectResponse404 = {
  data: ResourceNotFoundResponse;
  status: 404;
};

export type createInstrumentProductDirectResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type createInstrumentProductDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type createInstrumentProductDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type createInstrumentProductDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type createInstrumentProductDirectResponseSuccess =
  createInstrumentProductDirectResponse201 & {
    headers: Headers;
  };
export type createInstrumentProductDirectResponseError = (
  | createInstrumentProductDirectResponse400
  | createInstrumentProductDirectResponse401
  | createInstrumentProductDirectResponse403
  | createInstrumentProductDirectResponse404
  | createInstrumentProductDirectResponse409
  | createInstrumentProductDirectResponse422
  | createInstrumentProductDirectResponse429
  | createInstrumentProductDirectResponse500
) & {
  headers: Headers;
};

export type createInstrumentProductDirectResponse =
  | createInstrumentProductDirectResponseSuccess
  | createInstrumentProductDirectResponseError;

export const getCreateInstrumentProductDirectUrl = (clientId: string) => {
  return `/api/v2/clients/${clientId}/instrument-products`;
};

/**
 * Creates an instrument product
 * @summary Creates an instrument product
 */
export const createInstrumentProductDirect = async (
  clientId: string,
  instrumentProductCreateRequest: InstrumentProductCreateRequest,
  options?: RequestInit,
): Promise<createInstrumentProductDirectResponse> => {
  return restFetch<createInstrumentProductDirectResponse>(
    getCreateInstrumentProductDirectUrl(clientId),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(instrumentProductCreateRequest),
    },
  );
};

export const getCreateInstrumentProductDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ResourceNotFoundResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createInstrumentProductDirect>>,
    TError,
    { clientId: string; data: InstrumentProductCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createInstrumentProductDirect>>,
  TError,
  { clientId: string; data: InstrumentProductCreateRequest },
  TContext
> => {
  const mutationKey = ["createInstrumentProductDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createInstrumentProductDirect>>,
    { clientId: string; data: InstrumentProductCreateRequest }
  > = props => {
    const { clientId, data } = props ?? {};

    return createInstrumentProductDirect(clientId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateInstrumentProductDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof createInstrumentProductDirect>>
>;
export type CreateInstrumentProductDirectMutationBody =
  InstrumentProductCreateRequest;
export type CreateInstrumentProductDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ResourceNotFoundResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Creates an instrument product
 */
export const useCreateInstrumentProductDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ResourceNotFoundResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createInstrumentProductDirect>>,
      TError,
      { clientId: string; data: InstrumentProductCreateRequest },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createInstrumentProductDirect>>,
  TError,
  { clientId: string; data: InstrumentProductCreateRequest },
  TContext
> => {
  return useMutation(
    getCreateInstrumentProductDirectMutationOptions(options),
    queryClient,
  );
};
export type getInstrumentProductsByIdDirectResponse200 = {
  data: InstrumentProduct;
  status: 200;
};

export type getInstrumentProductsByIdDirectResponse400 = {
  data: RequestBadRequestResponse;
  status: 400;
};

export type getInstrumentProductsByIdDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type getInstrumentProductsByIdDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type getInstrumentProductsByIdDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type getInstrumentProductsByIdDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type getInstrumentProductsByIdDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type getInstrumentProductsByIdDirectResponseSuccess =
  getInstrumentProductsByIdDirectResponse200 & {
    headers: Headers;
  };
export type getInstrumentProductsByIdDirectResponseError = (
  | getInstrumentProductsByIdDirectResponse400
  | getInstrumentProductsByIdDirectResponse401
  | getInstrumentProductsByIdDirectResponse403
  | getInstrumentProductsByIdDirectResponse404
  | getInstrumentProductsByIdDirectResponse429
  | getInstrumentProductsByIdDirectResponse500
) & {
  headers: Headers;
};

export type getInstrumentProductsByIdDirectResponse =
  | getInstrumentProductsByIdDirectResponseSuccess
  | getInstrumentProductsByIdDirectResponseError;

export const getGetInstrumentProductsByIdDirectUrl = (
  clientId: string,
  id: string,
) => {
  return `/api/v2/clients/${clientId}/instrument-products/${id}`;
};

/**
 * Retrieves instrument product.
 * @summary Retrieves a single instrument product
 */
export const getInstrumentProductsByIdDirect = async (
  clientId: string,
  id: string,
  options?: RequestInit,
): Promise<getInstrumentProductsByIdDirectResponse> => {
  return restFetch<getInstrumentProductsByIdDirectResponse>(
    getGetInstrumentProductsByIdDirectUrl(clientId, id),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetInstrumentProductsByIdDirectQueryKey = (
  clientId: string,
  id: string,
) => {
  return [`/api/v2/clients/${clientId}/instrument-products/${id}`] as const;
};

export const getGetInstrumentProductsByIdDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetInstrumentProductsByIdDirectQueryKey(clientId, id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>
  > = ({ signal }) =>
    getInstrumentProductsByIdDirect(clientId, id, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled:
      clientId !== null &&
      clientId !== undefined &&
      id !== null &&
      id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetInstrumentProductsByIdDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>
>;
export type GetInstrumentProductsByIdDirectQueryError =
  | RequestBadRequestResponse
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useGetInstrumentProductsByIdDirect<
  TData = Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
          TError,
          Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInstrumentProductsByIdDirect<
  TData = Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
          TError,
          Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInstrumentProductsByIdDirect<
  TData = Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Retrieves a single instrument product
 */

export function useGetInstrumentProductsByIdDirect<
  TData = Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  clientId: string,
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getInstrumentProductsByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetInstrumentProductsByIdDirectQueryOptions(
    clientId,
    id,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updateInstrumentProductDirectResponse200 = {
  data: InstrumentProduct;
  status: 200;
};

export type updateInstrumentProductDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type updateInstrumentProductDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type updateInstrumentProductDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type updateInstrumentProductDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type updateInstrumentProductDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type updateInstrumentProductDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type updateInstrumentProductDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type updateInstrumentProductDirectResponseSuccess =
  updateInstrumentProductDirectResponse200 & {
    headers: Headers;
  };
export type updateInstrumentProductDirectResponseError = (
  | updateInstrumentProductDirectResponse400
  | updateInstrumentProductDirectResponse401
  | updateInstrumentProductDirectResponse403
  | updateInstrumentProductDirectResponse404
  | updateInstrumentProductDirectResponse422
  | updateInstrumentProductDirectResponse429
  | updateInstrumentProductDirectResponse500
) & {
  headers: Headers;
};

export type updateInstrumentProductDirectResponse =
  | updateInstrumentProductDirectResponseSuccess
  | updateInstrumentProductDirectResponseError;

export const getUpdateInstrumentProductDirectUrl = (
  clientId: string,
  id: string,
) => {
  return `/api/v2/clients/${clientId}/instrument-products/${id}`;
};

/**
 * Updates an Insturment Product
 * @summary Updates an Instrument Product
 */
export const updateInstrumentProductDirect = async (
  clientId: string,
  id: string,
  instrumentProductUpdateRequest: InstrumentProductUpdateRequest,
  options?: RequestInit,
): Promise<updateInstrumentProductDirectResponse> => {
  return restFetch<updateInstrumentProductDirectResponse>(
    getUpdateInstrumentProductDirectUrl(clientId, id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(instrumentProductUpdateRequest),
    },
  );
};

export const getUpdateInstrumentProductDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateInstrumentProductDirect>>,
    TError,
    { clientId: string; id: string; data: InstrumentProductUpdateRequest },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateInstrumentProductDirect>>,
  TError,
  { clientId: string; id: string; data: InstrumentProductUpdateRequest },
  TContext
> => {
  const mutationKey = ["updateInstrumentProductDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateInstrumentProductDirect>>,
    { clientId: string; id: string; data: InstrumentProductUpdateRequest }
  > = props => {
    const { clientId, id, data } = props ?? {};

    return updateInstrumentProductDirect(clientId, id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateInstrumentProductDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateInstrumentProductDirect>>
>;
export type UpdateInstrumentProductDirectMutationBody =
  InstrumentProductUpdateRequest;
export type UpdateInstrumentProductDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Updates an Instrument Product
 */
export const useUpdateInstrumentProductDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateInstrumentProductDirect>>,
      TError,
      { clientId: string; id: string; data: InstrumentProductUpdateRequest },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateInstrumentProductDirect>>,
  TError,
  { clientId: string; id: string; data: InstrumentProductUpdateRequest },
  TContext
> => {
  return useMutation(
    getUpdateInstrumentProductDirectMutationOptions(options),
    queryClient,
  );
};
export type listClientBinRangesDirectResponse200 = {
  data: BinRangeList;
  status: 200;
};

export type listClientBinRangesDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type listClientBinRangesDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type listClientBinRangesDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type listClientBinRangesDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type listClientBinRangesDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type listClientBinRangesDirectResponseSuccess =
  listClientBinRangesDirectResponse200 & {
    headers: Headers;
  };
export type listClientBinRangesDirectResponseError = (
  | listClientBinRangesDirectResponse400
  | listClientBinRangesDirectResponse401
  | listClientBinRangesDirectResponse403
  | listClientBinRangesDirectResponse429
  | listClientBinRangesDirectResponse500
) & {
  headers: Headers;
};

export type listClientBinRangesDirectResponse =
  | listClientBinRangesDirectResponseSuccess
  | listClientBinRangesDirectResponseError;

export const getListClientBinRangesDirectUrl = (
  params?: ListClientBinRangesDirectParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v2/bin-ranges?${stringifiedParams}`
    : `/api/v2/bin-ranges`;
};

/**
 * **Internal-facing endpoint** used to retrieve all or a filtered set of BIN ranges
 * on the Thredd platform.
 *
 * This endpoint provides filtering capabilities for retrieving BIN range records.
 * You can use one or more query parameters to filter the results.
 *
 * ## Dependencies
 *
 * None.
 *
 * ## Filtering Behaviour
 *
 * - **No query parameters**: Returns all accessible BIN ranges
 * - **Single query parameter**: Filters BIN ranges matching that specific field value
 * - **Multiple query parameters**: Returns BIN ranges matching ALL specified criteria (AND logic)
 *
 * ## Searchable Fields
 *
 * The following query parameters can be used as filters:
 * - `client_id` - Filter by owner client UUID
 * - `bin_id` - Filter by BIN entity UUID
 * - `bin_min` - Filter by inferior limit of the BIN range (2 digit number)
 * - `bin_max` - Filter by superior limit of the BIN range (2 digit number)
 *
 * ## Use Cases
 *
 * - List all BIN ranges for a specific client
 * - Find BIN ranges by BIN entity
 * - Filter BIN ranges by upper or lower limit
 * @summary Search and list BIN ranges
 */
export const listClientBinRangesDirect = async (
  params?: ListClientBinRangesDirectParams,
  options?: RequestInit,
): Promise<listClientBinRangesDirectResponse> => {
  return restFetch<listClientBinRangesDirectResponse>(
    getListClientBinRangesDirectUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListClientBinRangesDirectQueryKey = (
  params?: ListClientBinRangesDirectParams,
) => {
  return [`/api/v2/bin-ranges`, ...(params ? [params] : [])] as const;
};

export const getListClientBinRangesDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof listClientBinRangesDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListClientBinRangesDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listClientBinRangesDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListClientBinRangesDirectQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listClientBinRangesDirect>>
  > = ({ signal }) =>
    listClientBinRangesDirect(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listClientBinRangesDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListClientBinRangesDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof listClientBinRangesDirect>>
>;
export type ListClientBinRangesDirectQueryError =
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useListClientBinRangesDirect<
  TData = Awaited<ReturnType<typeof listClientBinRangesDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params: undefined | ListClientBinRangesDirectParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listClientBinRangesDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listClientBinRangesDirect>>,
          TError,
          Awaited<ReturnType<typeof listClientBinRangesDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListClientBinRangesDirect<
  TData = Awaited<ReturnType<typeof listClientBinRangesDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListClientBinRangesDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listClientBinRangesDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listClientBinRangesDirect>>,
          TError,
          Awaited<ReturnType<typeof listClientBinRangesDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListClientBinRangesDirect<
  TData = Awaited<ReturnType<typeof listClientBinRangesDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListClientBinRangesDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listClientBinRangesDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Search and list BIN ranges
 */

export function useListClientBinRangesDirect<
  TData = Awaited<ReturnType<typeof listClientBinRangesDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListClientBinRangesDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listClientBinRangesDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListClientBinRangesDirectQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type createBinRangeDirectResponse201 = {
  data: BinRange;
  status: 201;
};

export type createBinRangeDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type createBinRangeDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type createBinRangeDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type createBinRangeDirectResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type createBinRangeDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type createBinRangeDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type createBinRangeDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type createBinRangeDirectResponseSuccess =
  createBinRangeDirectResponse201 & {
    headers: Headers;
  };
export type createBinRangeDirectResponseError = (
  | createBinRangeDirectResponse400
  | createBinRangeDirectResponse401
  | createBinRangeDirectResponse403
  | createBinRangeDirectResponse409
  | createBinRangeDirectResponse422
  | createBinRangeDirectResponse429
  | createBinRangeDirectResponse500
) & {
  headers: Headers;
};

export type createBinRangeDirectResponse =
  | createBinRangeDirectResponseSuccess
  | createBinRangeDirectResponseError;

export const getCreateBinRangeDirectUrl = () => {
  return `/api/v2/bin-ranges`;
};

/**
 * **Internal-facing endpoint.**
 *
 * This endpoint creates a BIN range - the range provided by the third party for generating the
 * PAN number (the 16-digit number found on payment cards). The BIN is the Bank Identification
 * Number (BIN), which comprises the first 6 digits and identifies the issuing bank or financial
 * institution. The BIN range specifies the upper and lower limits of the final two digits in the
 * specified BIN that can then be used when creating a PAN for a product.
 *
 * ## Dependencies
 *
 * - **client_id** - a BIN range must be associated with an MSA- or sub-client.
 * - **bin_id** - a BIN range must be associated with a BIN entity (from Reference Data Service).
 * @summary Create a BIN Range
 */
export const createBinRangeDirect = async (
  binRange: BinRange,
  options?: RequestInit,
): Promise<createBinRangeDirectResponse> => {
  return restFetch<createBinRangeDirectResponse>(getCreateBinRangeDirectUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(binRange),
  });
};

export const getCreateBinRangeDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createBinRangeDirect>>,
    TError,
    { data: BinRange },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createBinRangeDirect>>,
  TError,
  { data: BinRange },
  TContext
> => {
  const mutationKey = ["createBinRangeDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createBinRangeDirect>>,
    { data: BinRange }
  > = props => {
    const { data } = props ?? {};

    return createBinRangeDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateBinRangeDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof createBinRangeDirect>>
>;
export type CreateBinRangeDirectMutationBody = BinRange;
export type CreateBinRangeDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Create a BIN Range
 */
export const useCreateBinRangeDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createBinRangeDirect>>,
      TError,
      { data: BinRange },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createBinRangeDirect>>,
  TError,
  { data: BinRange },
  TContext
> => {
  return useMutation(
    getCreateBinRangeDirectMutationOptions(options),
    queryClient,
  );
};
export type getBinRangeDirectResponse200 = {
  data: BinRange;
  status: 200;
};

export type getBinRangeDirectResponse400 = {
  data: RequestBadRequestResponse;
  status: 400;
};

export type getBinRangeDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type getBinRangeDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type getBinRangeDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type getBinRangeDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type getBinRangeDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type getBinRangeDirectResponseSuccess = getBinRangeDirectResponse200 & {
  headers: Headers;
};
export type getBinRangeDirectResponseError = (
  | getBinRangeDirectResponse400
  | getBinRangeDirectResponse401
  | getBinRangeDirectResponse403
  | getBinRangeDirectResponse404
  | getBinRangeDirectResponse429
  | getBinRangeDirectResponse500
) & {
  headers: Headers;
};

export type getBinRangeDirectResponse =
  | getBinRangeDirectResponseSuccess
  | getBinRangeDirectResponseError;

export const getGetBinRangeDirectUrl = (id: string) => {
  return `/api/v2/bin-ranges/${id}`;
};

/**
 * **Internal-facing endpoint.**
 *
 * This endpoint used to retrieve a BIN range linked to a client onboarded on the Thredd platform.
 *
 * ## Dependencies
 *
 * None.
 * @summary Retrieve a Single BIN Range
 */
export const getBinRangeDirect = async (
  id: string,
  options?: RequestInit,
): Promise<getBinRangeDirectResponse> => {
  return restFetch<getBinRangeDirectResponse>(getGetBinRangeDirectUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetBinRangeDirectQueryKey = (id: string) => {
  return [`/api/v2/bin-ranges/${id}`] as const;
};

export const getGetBinRangeDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof getBinRangeDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBinRangeDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBinRangeDirectQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBinRangeDirect>>
  > = ({ signal }) => getBinRangeDirect(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getBinRangeDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBinRangeDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBinRangeDirect>>
>;
export type GetBinRangeDirectQueryError =
  | RequestBadRequestResponse
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useGetBinRangeDirect<
  TData = Awaited<ReturnType<typeof getBinRangeDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBinRangeDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBinRangeDirect>>,
          TError,
          Awaited<ReturnType<typeof getBinRangeDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBinRangeDirect<
  TData = Awaited<ReturnType<typeof getBinRangeDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBinRangeDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBinRangeDirect>>,
          TError,
          Awaited<ReturnType<typeof getBinRangeDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBinRangeDirect<
  TData = Awaited<ReturnType<typeof getBinRangeDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBinRangeDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Retrieve a Single BIN Range
 */

export function useGetBinRangeDirect<
  TData = Awaited<ReturnType<typeof getBinRangeDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBinRangeDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetBinRangeDirectQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type searchBinRangesDirectResponse200 = {
  data: BinRangeList;
  status: 200;
};

export type searchBinRangesDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type searchBinRangesDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type searchBinRangesDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type searchBinRangesDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type searchBinRangesDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type searchBinRangesDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type searchBinRangesDirectResponseSuccess =
  searchBinRangesDirectResponse200 & {
    headers: Headers;
  };
export type searchBinRangesDirectResponseError = (
  | searchBinRangesDirectResponse400
  | searchBinRangesDirectResponse401
  | searchBinRangesDirectResponse403
  | searchBinRangesDirectResponse422
  | searchBinRangesDirectResponse429
  | searchBinRangesDirectResponse500
) & {
  headers: Headers;
};

export type searchBinRangesDirectResponse =
  | searchBinRangesDirectResponseSuccess
  | searchBinRangesDirectResponseError;

export const getSearchBinRangesDirectUrl = () => {
  return `/api/v2/bin-ranges/search`;
};

/**
 * **Deprecated.** Use `GET /bin-ranges` with query parameters instead.
 *
 * **Internal-facing endpoint.**
 *
 * This endpoint is used to retrieve all or a filtered set of relevant BIN ranges on the Thredd
 * platform. If no field is specified in the request body, all BIN ranges are returned.
 *
 * You can use one or more fields in the request as a search criteria in order to filter the results.
 *
 * ## Dependencies
 *
 * None.
 * @deprecated
 * @summary List BIN Ranges (deprecated)
 */
export const searchBinRangesDirect = async (
  binRangeSearchRequest?: BinRangeSearchRequest,
  options?: RequestInit,
): Promise<searchBinRangesDirectResponse> => {
  return restFetch<searchBinRangesDirectResponse>(
    getSearchBinRangesDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(binRangeSearchRequest),
    },
  );
};

export const getSearchBinRangesDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchBinRangesDirect>>,
    TError,
    { data?: BinRangeSearchRequest },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchBinRangesDirect>>,
  TError,
  { data?: BinRangeSearchRequest },
  TContext
> => {
  const mutationKey = ["searchBinRangesDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchBinRangesDirect>>,
    { data?: BinRangeSearchRequest }
  > = props => {
    const { data } = props ?? {};

    return searchBinRangesDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SearchBinRangesDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchBinRangesDirect>>
>;
export type SearchBinRangesDirectMutationBody =
  | BinRangeSearchRequest
  | undefined;
export type SearchBinRangesDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @deprecated
 * @summary List BIN Ranges (deprecated)
 */
export const useSearchBinRangesDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof searchBinRangesDirect>>,
      TError,
      { data?: BinRangeSearchRequest },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof searchBinRangesDirect>>,
  TError,
  { data?: BinRangeSearchRequest },
  TContext
> => {
  return useMutation(
    getSearchBinRangesDirectMutationOptions(options),
    queryClient,
  );
};
export type listTransactionReasonCodesDirectResponse200 = {
  data: TransactionReasonCodeList;
  status: 200;
};

export type listTransactionReasonCodesDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type listTransactionReasonCodesDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type listTransactionReasonCodesDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type listTransactionReasonCodesDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type listTransactionReasonCodesDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type listTransactionReasonCodesDirectResponseSuccess =
  listTransactionReasonCodesDirectResponse200 & {
    headers: Headers;
  };
export type listTransactionReasonCodesDirectResponseError = (
  | listTransactionReasonCodesDirectResponse400
  | listTransactionReasonCodesDirectResponse401
  | listTransactionReasonCodesDirectResponse403
  | listTransactionReasonCodesDirectResponse429
  | listTransactionReasonCodesDirectResponse500
) & {
  headers: Headers;
};

export type listTransactionReasonCodesDirectResponse =
  | listTransactionReasonCodesDirectResponseSuccess
  | listTransactionReasonCodesDirectResponseError;

export const getListTransactionReasonCodesDirectUrl = (
  params?: ListTransactionReasonCodesDirectParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v2/transaction-reason-codes?${stringifiedParams}`
    : `/api/v2/transaction-reason-codes`;
};

/**
 * **Internal-facing endpoint** used to search and list transaction reason codes
 * with optional filtering.
 *
 * This endpoint provides filtering capabilities for retrieving transaction reason code records.
 * You can use one or more query parameters to filter the results.
 *
 * ## Business Rules
 *
 * - Transaction reason codes are defined only by **sub-clients** but can be seen by their **MSA clients**
 * - If no query parameters are provided, all accessible transaction reason codes are returned
 * - Multiple parameters are combined with AND logic (all must match)
 *
 * ## Dependencies
 *
 * None.
 *
 * ## Filtering Behaviour
 *
 * - **No query parameters**: Returns all accessible transaction reason codes
 * - **Single query parameter**: Filters codes matching that specific field value
 * - **Multiple query parameters**: Returns codes matching ALL specified criteria (AND logic)
 *
 * ## Searchable Fields
 *
 * The following query parameters can be used as filters:
 * - `client_id` - Filter by owner client UUID
 * - `reason_code` - Filter by reason code value (exact match)
 * - `transaction_type` - Filter by transaction type (Balance Adjustment, Load, Fee, Purchase)
 *
 * ## Use Cases
 *
 * - List all transaction reason codes for a specific client
 * - Find codes by transaction type for accounting categorization
 * - Discover available reason codes before creating manual transactions
 * @summary Search and list transaction reason codes
 */
export const listTransactionReasonCodesDirect = async (
  params?: ListTransactionReasonCodesDirectParams,
  options?: RequestInit,
): Promise<listTransactionReasonCodesDirectResponse> => {
  return restFetch<listTransactionReasonCodesDirectResponse>(
    getListTransactionReasonCodesDirectUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListTransactionReasonCodesDirectQueryKey = (
  params?: ListTransactionReasonCodesDirectParams,
) => {
  return [
    `/api/v2/transaction-reason-codes`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListTransactionReasonCodesDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListTransactionReasonCodesDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListTransactionReasonCodesDirectQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>
  > = ({ signal }) =>
    listTransactionReasonCodesDirect(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListTransactionReasonCodesDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>
>;
export type ListTransactionReasonCodesDirectQueryError =
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useListTransactionReasonCodesDirect<
  TData = Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params: undefined | ListTransactionReasonCodesDirectParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
          TError,
          Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListTransactionReasonCodesDirect<
  TData = Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListTransactionReasonCodesDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
          TError,
          Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListTransactionReasonCodesDirect<
  TData = Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListTransactionReasonCodesDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Search and list transaction reason codes
 */

export function useListTransactionReasonCodesDirect<
  TData = Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListTransactionReasonCodesDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listTransactionReasonCodesDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListTransactionReasonCodesDirectQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type createTransactionReasonCodeDirectResponse201 = {
  data: TransactionReasonCode;
  status: 201;
};

export type createTransactionReasonCodeDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type createTransactionReasonCodeDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type createTransactionReasonCodeDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type createTransactionReasonCodeDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type createTransactionReasonCodeDirectResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type createTransactionReasonCodeDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type createTransactionReasonCodeDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type createTransactionReasonCodeDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type createTransactionReasonCodeDirectResponseSuccess =
  createTransactionReasonCodeDirectResponse201 & {
    headers: Headers;
  };
export type createTransactionReasonCodeDirectResponseError = (
  | createTransactionReasonCodeDirectResponse400
  | createTransactionReasonCodeDirectResponse401
  | createTransactionReasonCodeDirectResponse403
  | createTransactionReasonCodeDirectResponse404
  | createTransactionReasonCodeDirectResponse409
  | createTransactionReasonCodeDirectResponse422
  | createTransactionReasonCodeDirectResponse429
  | createTransactionReasonCodeDirectResponse500
) & {
  headers: Headers;
};

export type createTransactionReasonCodeDirectResponse =
  | createTransactionReasonCodeDirectResponseSuccess
  | createTransactionReasonCodeDirectResponseError;

export const getCreateTransactionReasonCodeDirectUrl = () => {
  return `/api/v2/transaction-reason-codes`;
};

/**
 * **Internal-facing endpoint** used to create a new transaction reason code.
 *
 * Creates a transaction reason code that can be used to categorize manual transactions
 * for accounting and reporting purposes.
 *
 * ## Business Rules
 *
 * - Transaction reason codes are defined only by **sub-clients** but can be seen by their **MSA clients**
 * - Transaction reason codes are optional but useful for categorizing manual transactions
 * - Common use cases: marking transactions as "Refund", "Dispute", "Fee Adjustment", etc.
 * - The reason_code must be unique within the client's transaction reason codes
 *
 * ## Dependencies
 *
 * - **client_id**: Must be an existing MSA or sub-client
 *
 * ## Related Endpoints
 *
 * - Use `GET /api/v2/transaction-reason-codes/{id}` to retrieve a specific code
 * - Use `PATCH /api/v2/transaction-reason-codes/{id}` to update an existing code
 * - Use `GET /api/v2/transaction-reason-codes` to list/search codes
 * @summary Create a new transaction reason code
 */
export const createTransactionReasonCodeDirect = async (
  transactionReasonCode: TransactionReasonCode,
  options?: RequestInit,
): Promise<createTransactionReasonCodeDirectResponse> => {
  return restFetch<createTransactionReasonCodeDirectResponse>(
    getCreateTransactionReasonCodeDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(transactionReasonCode),
    },
  );
};

export const getCreateTransactionReasonCodeDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTransactionReasonCodeDirect>>,
    TError,
    { data: TransactionReasonCode },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createTransactionReasonCodeDirect>>,
  TError,
  { data: TransactionReasonCode },
  TContext
> => {
  const mutationKey = ["createTransactionReasonCodeDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createTransactionReasonCodeDirect>>,
    { data: TransactionReasonCode }
  > = props => {
    const { data } = props ?? {};

    return createTransactionReasonCodeDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTransactionReasonCodeDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof createTransactionReasonCodeDirect>>
>;
export type CreateTransactionReasonCodeDirectMutationBody =
  TransactionReasonCode;
export type CreateTransactionReasonCodeDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Create a new transaction reason code
 */
export const useCreateTransactionReasonCodeDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createTransactionReasonCodeDirect>>,
      TError,
      { data: TransactionReasonCode },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createTransactionReasonCodeDirect>>,
  TError,
  { data: TransactionReasonCode },
  TContext
> => {
  return useMutation(
    getCreateTransactionReasonCodeDirectMutationOptions(options),
    queryClient,
  );
};
export type getTransactionReasonCodeByIdDirectResponse200 = {
  data: TransactionReasonCode;
  status: 200;
};

export type getTransactionReasonCodeByIdDirectResponse400 = {
  data: RequestBadRequestResponse;
  status: 400;
};

export type getTransactionReasonCodeByIdDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type getTransactionReasonCodeByIdDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type getTransactionReasonCodeByIdDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type getTransactionReasonCodeByIdDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type getTransactionReasonCodeByIdDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type getTransactionReasonCodeByIdDirectResponseSuccess =
  getTransactionReasonCodeByIdDirectResponse200 & {
    headers: Headers;
  };
export type getTransactionReasonCodeByIdDirectResponseError = (
  | getTransactionReasonCodeByIdDirectResponse400
  | getTransactionReasonCodeByIdDirectResponse401
  | getTransactionReasonCodeByIdDirectResponse403
  | getTransactionReasonCodeByIdDirectResponse404
  | getTransactionReasonCodeByIdDirectResponse429
  | getTransactionReasonCodeByIdDirectResponse500
) & {
  headers: Headers;
};

export type getTransactionReasonCodeByIdDirectResponse =
  | getTransactionReasonCodeByIdDirectResponseSuccess
  | getTransactionReasonCodeByIdDirectResponseError;

export const getGetTransactionReasonCodeByIdDirectUrl = (id: string) => {
  return `/api/v2/transaction-reason-codes/${id}`;
};

/**
 * **Internal-facing endpoint** used to retrieve a specific transaction reason code by its ID.
 *
 * ## Business Rules
 *
 * - Transaction reason codes are defined only by **sub-clients** but can be seen by their **MSA clients**
 * - Returns all details of the transaction reason code including audit fields
 * - MSA clients can view transaction reason codes created by their sub-clients
 *
 * ## Use Cases
 *
 * - Viewing details of a specific transaction reason code
 * - Verifying transaction reason code configuration
 * - Retrieving current version for optimistic locking before updates
 * @summary Retrieve a single transaction reason code
 */
export const getTransactionReasonCodeByIdDirect = async (
  id: string,
  options?: RequestInit,
): Promise<getTransactionReasonCodeByIdDirectResponse> => {
  return restFetch<getTransactionReasonCodeByIdDirectResponse>(
    getGetTransactionReasonCodeByIdDirectUrl(id),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetTransactionReasonCodeByIdDirectQueryKey = (id: string) => {
  return [`/api/v2/transaction-reason-codes/${id}`] as const;
};

export const getGetTransactionReasonCodeByIdDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTransactionReasonCodeByIdDirectQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>
  > = ({ signal }) =>
    getTransactionReasonCodeByIdDirect(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTransactionReasonCodeByIdDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>
>;
export type GetTransactionReasonCodeByIdDirectQueryError =
  | RequestBadRequestResponse
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useGetTransactionReasonCodeByIdDirect<
  TData = Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
          TError,
          Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionReasonCodeByIdDirect<
  TData = Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
          TError,
          Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionReasonCodeByIdDirect<
  TData = Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Retrieve a single transaction reason code
 */

export function useGetTransactionReasonCodeByIdDirect<
  TData = Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTransactionReasonCodeByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetTransactionReasonCodeByIdDirectQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updateTransactionReasonCodeDirectResponse200 = {
  data: TransactionReasonCode;
  status: 200;
};

export type updateTransactionReasonCodeDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type updateTransactionReasonCodeDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type updateTransactionReasonCodeDirectResponse403 = {
  data: ProblemDetails;
  status: 403;
};

export type updateTransactionReasonCodeDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type updateTransactionReasonCodeDirectResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type updateTransactionReasonCodeDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type updateTransactionReasonCodeDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type updateTransactionReasonCodeDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type updateTransactionReasonCodeDirectResponseSuccess =
  updateTransactionReasonCodeDirectResponse200 & {
    headers: Headers;
  };
export type updateTransactionReasonCodeDirectResponseError = (
  | updateTransactionReasonCodeDirectResponse400
  | updateTransactionReasonCodeDirectResponse401
  | updateTransactionReasonCodeDirectResponse403
  | updateTransactionReasonCodeDirectResponse404
  | updateTransactionReasonCodeDirectResponse409
  | updateTransactionReasonCodeDirectResponse422
  | updateTransactionReasonCodeDirectResponse429
  | updateTransactionReasonCodeDirectResponse500
) & {
  headers: Headers;
};

export type updateTransactionReasonCodeDirectResponse =
  | updateTransactionReasonCodeDirectResponseSuccess
  | updateTransactionReasonCodeDirectResponseError;

export const getUpdateTransactionReasonCodeDirectUrl = (id: string) => {
  return `/api/v2/transaction-reason-codes/${id}`;
};

/**
 * **Internal-facing endpoint** used to update an existing transaction reason code.
 *
 * Updates one or more fields of a transaction reason code. Only the fields provided in
 * the request body will be updated; other fields remain unchanged.
 *
 * ## Business Rules
 *
 * - Transaction reason codes are defined only by **sub-clients** but can be seen by their **MSA clients**
 * - For sub-clients, transaction reason codes can only be updated for clients with the **LOCAL** configuration flag set
 * - The **client_id** is **immutable** and cannot be changed (a transaction reason code cannot be moved between clients)
 * - All fields in the request body are optional (partial updates supported)
 * - The version field is automatically incremented on each update for optimistic locking
 *
 * ## Important Notes
 *
 * - **Immutable Fields**: client_id cannot be updated after creation
 * - **Read-only Fields**: id, version, created_at, created_by, updated_at, updated_by are managed by the system
 * - Include the current version when updating to prevent concurrent modification issues
 *
 * ## Use Cases
 *
 * - Updating the description of an existing reason code
 * - Changing the reason_code value
 * - Modifying the transaction_type category
 * @summary Update an existing transaction reason code
 */
export const updateTransactionReasonCodeDirect = async (
  id: string,
  transactionReasonCode: TransactionReasonCode,
  options?: RequestInit,
): Promise<updateTransactionReasonCodeDirectResponse> => {
  return restFetch<updateTransactionReasonCodeDirectResponse>(
    getUpdateTransactionReasonCodeDirectUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(transactionReasonCode),
    },
  );
};

export const getUpdateTransactionReasonCodeDirectMutationOptions = <
  TError =
    | ProblemDetails
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTransactionReasonCodeDirect>>,
    TError,
    { id: string; data: TransactionReasonCode },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateTransactionReasonCodeDirect>>,
  TError,
  { id: string; data: TransactionReasonCode },
  TContext
> => {
  const mutationKey = ["updateTransactionReasonCodeDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateTransactionReasonCodeDirect>>,
    { id: string; data: TransactionReasonCode }
  > = props => {
    const { id, data } = props ?? {};

    return updateTransactionReasonCodeDirect(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTransactionReasonCodeDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateTransactionReasonCodeDirect>>
>;
export type UpdateTransactionReasonCodeDirectMutationBody =
  TransactionReasonCode;
export type UpdateTransactionReasonCodeDirectMutationError =
  | ProblemDetails
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Update an existing transaction reason code
 */
export const useUpdateTransactionReasonCodeDirect = <
  TError =
    | ProblemDetails
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateTransactionReasonCodeDirect>>,
      TError,
      { id: string; data: TransactionReasonCode },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateTransactionReasonCodeDirect>>,
  TError,
  { id: string; data: TransactionReasonCode },
  TContext
> => {
  return useMutation(
    getUpdateTransactionReasonCodeDirectMutationOptions(options),
    queryClient,
  );
};
export type deleteTransactionReasonCodeByIdDirectResponse200 = {
  data: TransactionReasonCode;
  status: 200;
};

export type deleteTransactionReasonCodeByIdDirectResponse400 = {
  data: RequestBadRequestResponse;
  status: 400;
};

export type deleteTransactionReasonCodeByIdDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type deleteTransactionReasonCodeByIdDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type deleteTransactionReasonCodeByIdDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type deleteTransactionReasonCodeByIdDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type deleteTransactionReasonCodeByIdDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type deleteTransactionReasonCodeByIdDirectResponseSuccess =
  deleteTransactionReasonCodeByIdDirectResponse200 & {
    headers: Headers;
  };
export type deleteTransactionReasonCodeByIdDirectResponseError = (
  | deleteTransactionReasonCodeByIdDirectResponse400
  | deleteTransactionReasonCodeByIdDirectResponse401
  | deleteTransactionReasonCodeByIdDirectResponse403
  | deleteTransactionReasonCodeByIdDirectResponse404
  | deleteTransactionReasonCodeByIdDirectResponse429
  | deleteTransactionReasonCodeByIdDirectResponse500
) & {
  headers: Headers;
};

export type deleteTransactionReasonCodeByIdDirectResponse =
  | deleteTransactionReasonCodeByIdDirectResponseSuccess
  | deleteTransactionReasonCodeByIdDirectResponseError;

export const getDeleteTransactionReasonCodeByIdDirectUrl = (id: string) => {
  return `/api/v2/transaction-reason-codes/${id}`;
};

/**
 * **Internal-facing endpoint** used to delete a transaction reason code.
 *
 * Deletes the specified transaction reason code from the system. The transaction reason code
 * record is removed, but historical references are preserved.
 *
 * ## Business Rules
 *
 * - Transaction reason codes are defined only by **sub-clients** but can be seen by their **MSA clients**
 * - Deleting a transaction reason code **does not delete** any references to that code in existing transactions
 * - Historical transactions will retain their transaction reason code references
 * - The deletion is permanent and cannot be undone
 *
 * ## Important Notes
 *
 * - **Historical Data**: Existing transactions that used this code will retain the reference
 * - **Audit Trail**: The code's data is returned in the response for audit purposes
 * - **No Cascade**: Deletion does not affect related transactions
 *
 * ## Use Cases
 *
 * - Removing obsolete or unused transaction reason codes
 * - Cleaning up incorrectly created codes
 * - Archiving old categorization schemes
 * @summary Delete a transaction reason code
 */
export const deleteTransactionReasonCodeByIdDirect = async (
  id: string,
  options?: RequestInit,
): Promise<deleteTransactionReasonCodeByIdDirectResponse> => {
  return restFetch<deleteTransactionReasonCodeByIdDirectResponse>(
    getDeleteTransactionReasonCodeByIdDirectUrl(id),
    {
      ...options,
      method: "DELETE",
    },
  );
};

export const getDeleteTransactionReasonCodeByIdDirectMutationOptions = <
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTransactionReasonCodeByIdDirect>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTransactionReasonCodeByIdDirect>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["deleteTransactionReasonCodeByIdDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTransactionReasonCodeByIdDirect>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteTransactionReasonCodeByIdDirect(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTransactionReasonCodeByIdDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTransactionReasonCodeByIdDirect>>
>;

export type DeleteTransactionReasonCodeByIdDirectMutationError =
  | RequestBadRequestResponse
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

/**
 * @summary Delete a transaction reason code
 */
export const useDeleteTransactionReasonCodeByIdDirect = <
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteTransactionReasonCodeByIdDirect>>,
      TError,
      { id: string },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteTransactionReasonCodeByIdDirect>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(
    getDeleteTransactionReasonCodeByIdDirectMutationOptions(options),
    queryClient,
  );
};
export type searchTransactionReasonCodesDirectResponse200 = {
  data: TransactionReasonCodeList;
  status: 200;
};

export type searchTransactionReasonCodesDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type searchTransactionReasonCodesDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type searchTransactionReasonCodesDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type searchTransactionReasonCodesDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type searchTransactionReasonCodesDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type searchTransactionReasonCodesDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type searchTransactionReasonCodesDirectResponseSuccess =
  searchTransactionReasonCodesDirectResponse200 & {
    headers: Headers;
  };
export type searchTransactionReasonCodesDirectResponseError = (
  | searchTransactionReasonCodesDirectResponse400
  | searchTransactionReasonCodesDirectResponse401
  | searchTransactionReasonCodesDirectResponse403
  | searchTransactionReasonCodesDirectResponse422
  | searchTransactionReasonCodesDirectResponse429
  | searchTransactionReasonCodesDirectResponse500
) & {
  headers: Headers;
};

export type searchTransactionReasonCodesDirectResponse =
  | searchTransactionReasonCodesDirectResponseSuccess
  | searchTransactionReasonCodesDirectResponseError;

export const getSearchTransactionReasonCodesDirectUrl = () => {
  return `/api/v2/transaction-reason-codes/search`;
};

/**
 * **Deprecated.** Use `GET /transaction-reason-codes` with query parameters instead.
 * **Internal-facing endpoint** used to search and list transaction reason codes with optional filtering.
 *
 * This endpoint functions as a flexible search and list operation. You can provide one or more
 * search criteria to filter results, or omit all criteria to retrieve all transaction reason codes.
 *
 * ## Business Rules
 *
 * - Transaction reason codes are defined only by **sub-clients** but can be seen by their **MSA clients**
 * - If no search criteria are provided, **all** transaction reason codes are returned
 * - Multiple criteria are combined with AND logic (all must match)
 * - Results include full transaction reason code details with audit information
 *
 * ## Search Behavior
 *
 * - **Exact Matching**: Most fields use exact matching (id, client_id, reason_code, transaction_type)
 * - **Partial Matching**: The description field may support partial matching
 * - **Empty Request**: Sending an empty request body returns all codes
 * - **Multiple Results**: Results are returned as an array, even if only one match
 *
 * ## Use Cases
 *
 * - Listing all transaction reason codes for a client
 * - Finding codes by specific criteria
 * - Discovering available reason codes before creating transactions
 * - Auditing transaction reason code usage
 * @deprecated
 * @summary Search and list transaction reason codes (deprecated)
 */
export const searchTransactionReasonCodesDirect = async (
  transactionReasonCode: TransactionReasonCode,
  options?: RequestInit,
): Promise<searchTransactionReasonCodesDirectResponse> => {
  return restFetch<searchTransactionReasonCodesDirectResponse>(
    getSearchTransactionReasonCodesDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(transactionReasonCode),
    },
  );
};

export const getSearchTransactionReasonCodesDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchTransactionReasonCodesDirect>>,
    TError,
    { data: TransactionReasonCode },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchTransactionReasonCodesDirect>>,
  TError,
  { data: TransactionReasonCode },
  TContext
> => {
  const mutationKey = ["searchTransactionReasonCodesDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchTransactionReasonCodesDirect>>,
    { data: TransactionReasonCode }
  > = props => {
    const { data } = props ?? {};

    return searchTransactionReasonCodesDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SearchTransactionReasonCodesDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchTransactionReasonCodesDirect>>
>;
export type SearchTransactionReasonCodesDirectMutationBody =
  TransactionReasonCode;
export type SearchTransactionReasonCodesDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @deprecated
 * @summary Search and list transaction reason codes (deprecated)
 */
export const useSearchTransactionReasonCodesDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof searchTransactionReasonCodesDirect>>,
      TError,
      { data: TransactionReasonCode },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof searchTransactionReasonCodesDirect>>,
  TError,
  { data: TransactionReasonCode },
  TContext
> => {
  return useMutation(
    getSearchTransactionReasonCodesDirectMutationOptions(options),
    queryClient,
  );
};
export type listPolicyGroupsDirectResponse200 = {
  data: PolicyGroupList;
  status: 200;
};

export type listPolicyGroupsDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type listPolicyGroupsDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type listPolicyGroupsDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type listPolicyGroupsDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type listPolicyGroupsDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type listPolicyGroupsDirectResponseSuccess =
  listPolicyGroupsDirectResponse200 & {
    headers: Headers;
  };
export type listPolicyGroupsDirectResponseError = (
  | listPolicyGroupsDirectResponse400
  | listPolicyGroupsDirectResponse401
  | listPolicyGroupsDirectResponse403
  | listPolicyGroupsDirectResponse429
  | listPolicyGroupsDirectResponse500
) & {
  headers: Headers;
};

export type listPolicyGroupsDirectResponse =
  | listPolicyGroupsDirectResponseSuccess
  | listPolicyGroupsDirectResponseError;

export const getListPolicyGroupsDirectUrl = (
  params?: ListPolicyGroupsDirectParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v2/policy-groups?${stringifiedParams}`
    : `/api/v2/policy-groups`;
};

/**
 * **Internal-facing endpoint** used to retrieve all or a filtered set of policy groups
 * on the Thredd platform.
 *
 * This endpoint provides filtering capabilities for retrieving policy group records.
 * You can use one or more query parameters to filter the results.
 *
 * ## Dependencies
 *
 * None.
 *
 * ## Filtering Behaviour
 *
 * - **No query parameters**: Returns all accessible policy groups
 * - **Single query parameter**: Filters policy groups matching that specific field value
 * - **Multiple query parameters**: Returns policy groups matching ALL specified criteria (AND logic)
 *
 * ## Searchable Fields
 *
 * The following query parameters can be used as filters:
 * - `client_id` - Filter by owner client UUID
 * - `name` - Filter by policy group name (supports partial matching)
 * - `group` - Filter by group type (e.g. ACCOUNT_HOLDER_NUMBER_GENERATION, ACCOUNT_NUMBER_GENERATION)
 * - `status` - Filter by status (DRAFT, ACTIVE, INACTIVE)
 * - `financial_product_id` - Filter by associated financial product UUID
 * - `instrument_product_id` - Filter by associated instrument product UUID
 *
 * ## Use Cases
 *
 * - List all policy groups for a specific client
 * - Find policy groups by group type for configuration purposes
 * - Filter policy groups by operational status
 * - Retrieve policy groups linked to a specific financial or instrument product
 * @summary Search and list policy groups
 */
export const listPolicyGroupsDirect = async (
  params?: ListPolicyGroupsDirectParams,
  options?: RequestInit,
): Promise<listPolicyGroupsDirectResponse> => {
  return restFetch<listPolicyGroupsDirectResponse>(
    getListPolicyGroupsDirectUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListPolicyGroupsDirectQueryKey = (
  params?: ListPolicyGroupsDirectParams,
) => {
  return [`/api/v2/policy-groups`, ...(params ? [params] : [])] as const;
};

export const getListPolicyGroupsDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListPolicyGroupsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListPolicyGroupsDirectQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listPolicyGroupsDirect>>
  > = ({ signal }) =>
    listPolicyGroupsDirect(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListPolicyGroupsDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPolicyGroupsDirect>>
>;
export type ListPolicyGroupsDirectQueryError =
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useListPolicyGroupsDirect<
  TData = Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params: undefined | ListPolicyGroupsDirectParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
          TError,
          Awaited<ReturnType<typeof listPolicyGroupsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListPolicyGroupsDirect<
  TData = Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListPolicyGroupsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
          TError,
          Awaited<ReturnType<typeof listPolicyGroupsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListPolicyGroupsDirect<
  TData = Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListPolicyGroupsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Search and list policy groups
 */

export function useListPolicyGroupsDirect<
  TData = Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListPolicyGroupsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listPolicyGroupsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListPolicyGroupsDirectQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type createPolicyGroupDirectResponse201 = {
  data: PolicyGroup;
  status: 201;
};

export type createPolicyGroupDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type createPolicyGroupDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type createPolicyGroupDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type createPolicyGroupDirectResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type createPolicyGroupDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type createPolicyGroupDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type createPolicyGroupDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type createPolicyGroupDirectResponseSuccess =
  createPolicyGroupDirectResponse201 & {
    headers: Headers;
  };
export type createPolicyGroupDirectResponseError = (
  | createPolicyGroupDirectResponse400
  | createPolicyGroupDirectResponse401
  | createPolicyGroupDirectResponse403
  | createPolicyGroupDirectResponse409
  | createPolicyGroupDirectResponse422
  | createPolicyGroupDirectResponse429
  | createPolicyGroupDirectResponse500
) & {
  headers: Headers;
};

export type createPolicyGroupDirectResponse =
  | createPolicyGroupDirectResponseSuccess
  | createPolicyGroupDirectResponseError;

export const getCreatePolicyGroupDirectUrl = () => {
  return `/api/v2/policy-groups`;
};

/**
 * Creates a Policy Group.
 * @summary Creates a Policy Group.
 */
export const createPolicyGroupDirect = async (
  policyGroup: PolicyGroup,
  options?: RequestInit,
): Promise<createPolicyGroupDirectResponse> => {
  return restFetch<createPolicyGroupDirectResponse>(
    getCreatePolicyGroupDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(policyGroup),
    },
  );
};

export const getCreatePolicyGroupDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPolicyGroupDirect>>,
    TError,
    { data: PolicyGroup },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPolicyGroupDirect>>,
  TError,
  { data: PolicyGroup },
  TContext
> => {
  const mutationKey = ["createPolicyGroupDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPolicyGroupDirect>>,
    { data: PolicyGroup }
  > = props => {
    const { data } = props ?? {};

    return createPolicyGroupDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePolicyGroupDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPolicyGroupDirect>>
>;
export type CreatePolicyGroupDirectMutationBody = PolicyGroup;
export type CreatePolicyGroupDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Creates a Policy Group.
 */
export const useCreatePolicyGroupDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPolicyGroupDirect>>,
      TError,
      { data: PolicyGroup },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createPolicyGroupDirect>>,
  TError,
  { data: PolicyGroup },
  TContext
> => {
  return useMutation(
    getCreatePolicyGroupDirectMutationOptions(options),
    queryClient,
  );
};
export type getPolicyGroupByIdDirectResponse200 = {
  data: PolicyGroup;
  status: 200;
};

export type getPolicyGroupByIdDirectResponse400 = {
  data: RequestBadRequestResponse;
  status: 400;
};

export type getPolicyGroupByIdDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type getPolicyGroupByIdDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type getPolicyGroupByIdDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type getPolicyGroupByIdDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type getPolicyGroupByIdDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type getPolicyGroupByIdDirectResponseSuccess =
  getPolicyGroupByIdDirectResponse200 & {
    headers: Headers;
  };
export type getPolicyGroupByIdDirectResponseError = (
  | getPolicyGroupByIdDirectResponse400
  | getPolicyGroupByIdDirectResponse401
  | getPolicyGroupByIdDirectResponse403
  | getPolicyGroupByIdDirectResponse404
  | getPolicyGroupByIdDirectResponse429
  | getPolicyGroupByIdDirectResponse500
) & {
  headers: Headers;
};

export type getPolicyGroupByIdDirectResponse =
  | getPolicyGroupByIdDirectResponseSuccess
  | getPolicyGroupByIdDirectResponseError;

export const getGetPolicyGroupByIdDirectUrl = (id: string) => {
  return `/api/v2/policy-groups/${id}`;
};

/**
 * Retrieves a Policy Group.
 * @summary Retrieves a Policy Group.
 */
export const getPolicyGroupByIdDirect = async (
  id: string,
  options?: RequestInit,
): Promise<getPolicyGroupByIdDirectResponse> => {
  return restFetch<getPolicyGroupByIdDirectResponse>(
    getGetPolicyGroupByIdDirectUrl(id),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetPolicyGroupByIdDirectQueryKey = (id: string) => {
  return [`/api/v2/policy-groups/${id}`] as const;
};

export const getGetPolicyGroupByIdDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetPolicyGroupByIdDirectQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>
  > = ({ signal }) =>
    getPolicyGroupByIdDirect(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPolicyGroupByIdDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>
>;
export type GetPolicyGroupByIdDirectQueryError =
  | RequestBadRequestResponse
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useGetPolicyGroupByIdDirect<
  TData = Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
          TError,
          Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPolicyGroupByIdDirect<
  TData = Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
          TError,
          Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPolicyGroupByIdDirect<
  TData = Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Retrieves a Policy Group.
 */

export function useGetPolicyGroupByIdDirect<
  TData = Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getPolicyGroupByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetPolicyGroupByIdDirectQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updatePolicyGroupDirectResponse200 = {
  data: PolicyGroup;
  status: 200;
};

export type updatePolicyGroupDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type updatePolicyGroupDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type updatePolicyGroupDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type updatePolicyGroupDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type updatePolicyGroupDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type updatePolicyGroupDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type updatePolicyGroupDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type updatePolicyGroupDirectResponseSuccess =
  updatePolicyGroupDirectResponse200 & {
    headers: Headers;
  };
export type updatePolicyGroupDirectResponseError = (
  | updatePolicyGroupDirectResponse400
  | updatePolicyGroupDirectResponse401
  | updatePolicyGroupDirectResponse403
  | updatePolicyGroupDirectResponse404
  | updatePolicyGroupDirectResponse422
  | updatePolicyGroupDirectResponse429
  | updatePolicyGroupDirectResponse500
) & {
  headers: Headers;
};

export type updatePolicyGroupDirectResponse =
  | updatePolicyGroupDirectResponseSuccess
  | updatePolicyGroupDirectResponseError;

export const getUpdatePolicyGroupDirectUrl = (id: string) => {
  return `/api/v2/policy-groups/${id}`;
};

/**
 * Updates a Policy Group.
 * @summary Updates a Policy Group.
 */
export const updatePolicyGroupDirect = async (
  id: string,
  policyGroup: PolicyGroup,
  options?: RequestInit,
): Promise<updatePolicyGroupDirectResponse> => {
  return restFetch<updatePolicyGroupDirectResponse>(
    getUpdatePolicyGroupDirectUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(policyGroup),
    },
  );
};

export const getUpdatePolicyGroupDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updatePolicyGroupDirect>>,
    TError,
    { id: string; data: PolicyGroup },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updatePolicyGroupDirect>>,
  TError,
  { id: string; data: PolicyGroup },
  TContext
> => {
  const mutationKey = ["updatePolicyGroupDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updatePolicyGroupDirect>>,
    { id: string; data: PolicyGroup }
  > = props => {
    const { id, data } = props ?? {};

    return updatePolicyGroupDirect(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdatePolicyGroupDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof updatePolicyGroupDirect>>
>;
export type UpdatePolicyGroupDirectMutationBody = PolicyGroup;
export type UpdatePolicyGroupDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Updates a Policy Group.
 */
export const useUpdatePolicyGroupDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updatePolicyGroupDirect>>,
      TError,
      { id: string; data: PolicyGroup },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updatePolicyGroupDirect>>,
  TError,
  { id: string; data: PolicyGroup },
  TContext
> => {
  return useMutation(
    getUpdatePolicyGroupDirectMutationOptions(options),
    queryClient,
  );
};
export type deletePolicyGroupDirectResponse200 = {
  data: PolicyGroup;
  status: 200;
};

export type deletePolicyGroupDirectResponse400 = {
  data: RequestBadRequestResponse;
  status: 400;
};

export type deletePolicyGroupDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type deletePolicyGroupDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type deletePolicyGroupDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type deletePolicyGroupDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type deletePolicyGroupDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type deletePolicyGroupDirectResponseSuccess =
  deletePolicyGroupDirectResponse200 & {
    headers: Headers;
  };
export type deletePolicyGroupDirectResponseError = (
  | deletePolicyGroupDirectResponse400
  | deletePolicyGroupDirectResponse401
  | deletePolicyGroupDirectResponse403
  | deletePolicyGroupDirectResponse404
  | deletePolicyGroupDirectResponse429
  | deletePolicyGroupDirectResponse500
) & {
  headers: Headers;
};

export type deletePolicyGroupDirectResponse =
  | deletePolicyGroupDirectResponseSuccess
  | deletePolicyGroupDirectResponseError;

export const getDeletePolicyGroupDirectUrl = (id: string) => {
  return `/api/v2/policy-groups/${id}`;
};

/**
 * Deletes a Policy Group.
 * @summary Deletes a Policy Group.
 */
export const deletePolicyGroupDirect = async (
  id: string,
  options?: RequestInit,
): Promise<deletePolicyGroupDirectResponse> => {
  return restFetch<deletePolicyGroupDirectResponse>(
    getDeletePolicyGroupDirectUrl(id),
    {
      ...options,
      method: "DELETE",
    },
  );
};

export const getDeletePolicyGroupDirectMutationOptions = <
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePolicyGroupDirect>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deletePolicyGroupDirect>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["deletePolicyGroupDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deletePolicyGroupDirect>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deletePolicyGroupDirect(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeletePolicyGroupDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof deletePolicyGroupDirect>>
>;

export type DeletePolicyGroupDirectMutationError =
  | RequestBadRequestResponse
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

/**
 * @summary Deletes a Policy Group.
 */
export const useDeletePolicyGroupDirect = <
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deletePolicyGroupDirect>>,
      TError,
      { id: string },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deletePolicyGroupDirect>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(
    getDeletePolicyGroupDirectMutationOptions(options),
    queryClient,
  );
};
export type searchPolicyGroupsDirectResponse200 = {
  data: PolicyGroupList;
  status: 200;
};

export type searchPolicyGroupsDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type searchPolicyGroupsDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type searchPolicyGroupsDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type searchPolicyGroupsDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type searchPolicyGroupsDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type searchPolicyGroupsDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type searchPolicyGroupsDirectResponseSuccess =
  searchPolicyGroupsDirectResponse200 & {
    headers: Headers;
  };
export type searchPolicyGroupsDirectResponseError = (
  | searchPolicyGroupsDirectResponse400
  | searchPolicyGroupsDirectResponse401
  | searchPolicyGroupsDirectResponse403
  | searchPolicyGroupsDirectResponse422
  | searchPolicyGroupsDirectResponse429
  | searchPolicyGroupsDirectResponse500
) & {
  headers: Headers;
};

export type searchPolicyGroupsDirectResponse =
  | searchPolicyGroupsDirectResponseSuccess
  | searchPolicyGroupsDirectResponseError;

export const getSearchPolicyGroupsDirectUrl = () => {
  return `/api/v2/policy-groups/search`;
};

/**
 * **Deprecated.** Use `GET /policy-groups` with query parameters instead.
 *
 * Search for Policy Groups.
 * @deprecated
 * @summary Search for Policy Groups. (deprecated)
 */
export const searchPolicyGroupsDirect = async (
  policyGroup: PolicyGroup,
  options?: RequestInit,
): Promise<searchPolicyGroupsDirectResponse> => {
  return restFetch<searchPolicyGroupsDirectResponse>(
    getSearchPolicyGroupsDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(policyGroup),
    },
  );
};

export const getSearchPolicyGroupsDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchPolicyGroupsDirect>>,
    TError,
    { data: PolicyGroup },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchPolicyGroupsDirect>>,
  TError,
  { data: PolicyGroup },
  TContext
> => {
  const mutationKey = ["searchPolicyGroupsDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchPolicyGroupsDirect>>,
    { data: PolicyGroup }
  > = props => {
    const { data } = props ?? {};

    return searchPolicyGroupsDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SearchPolicyGroupsDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchPolicyGroupsDirect>>
>;
export type SearchPolicyGroupsDirectMutationBody = PolicyGroup;
export type SearchPolicyGroupsDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @deprecated
 * @summary Search for Policy Groups. (deprecated)
 */
export const useSearchPolicyGroupsDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof searchPolicyGroupsDirect>>,
      TError,
      { data: PolicyGroup },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof searchPolicyGroupsDirect>>,
  TError,
  { data: PolicyGroup },
  TContext
> => {
  return useMutation(
    getSearchPolicyGroupsDirectMutationOptions(options),
    queryClient,
  );
};
export type getChannelByIdDirectResponse200 = {
  data: ChannelDirect;
  status: 200;
};

export type getChannelByIdDirectResponse400 = {
  data: RequestBadRequestResponse;
  status: 400;
};

export type getChannelByIdDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type getChannelByIdDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type getChannelByIdDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type getChannelByIdDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type getChannelByIdDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type getChannelByIdDirectResponseSuccess =
  getChannelByIdDirectResponse200 & {
    headers: Headers;
  };
export type getChannelByIdDirectResponseError = (
  | getChannelByIdDirectResponse400
  | getChannelByIdDirectResponse401
  | getChannelByIdDirectResponse403
  | getChannelByIdDirectResponse404
  | getChannelByIdDirectResponse429
  | getChannelByIdDirectResponse500
) & {
  headers: Headers;
};

export type getChannelByIdDirectResponse =
  | getChannelByIdDirectResponseSuccess
  | getChannelByIdDirectResponseError;

export const getGetChannelByIdDirectUrl = (id: string) => {
  return `/api/v2/channels/${id}`;
};

/**
 * Retrieves a channel
 * @summary Retrieves a single channel
 */
export const getChannelByIdDirect = async (
  id: string,
  options?: RequestInit,
): Promise<getChannelByIdDirectResponse> => {
  return restFetch<getChannelByIdDirectResponse>(
    getGetChannelByIdDirectUrl(id),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetChannelByIdDirectQueryKey = (id: string) => {
  return [`/api/v2/channels/${id}`] as const;
};

export const getGetChannelByIdDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof getChannelByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getChannelByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetChannelByIdDirectQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getChannelByIdDirect>>
  > = ({ signal }) => getChannelByIdDirect(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getChannelByIdDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetChannelByIdDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof getChannelByIdDirect>>
>;
export type GetChannelByIdDirectQueryError =
  | RequestBadRequestResponse
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useGetChannelByIdDirect<
  TData = Awaited<ReturnType<typeof getChannelByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getChannelByIdDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getChannelByIdDirect>>,
          TError,
          Awaited<ReturnType<typeof getChannelByIdDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetChannelByIdDirect<
  TData = Awaited<ReturnType<typeof getChannelByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getChannelByIdDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getChannelByIdDirect>>,
          TError,
          Awaited<ReturnType<typeof getChannelByIdDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetChannelByIdDirect<
  TData = Awaited<ReturnType<typeof getChannelByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getChannelByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Retrieves a single channel
 */

export function useGetChannelByIdDirect<
  TData = Awaited<ReturnType<typeof getChannelByIdDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getChannelByIdDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetChannelByIdDirectQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updateChannelDirectResponse200 = {
  data: ChannelDirect;
  status: 200;
};

export type updateChannelDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type updateChannelDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type updateChannelDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type updateChannelDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type updateChannelDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type updateChannelDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type updateChannelDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type updateChannelDirectResponseSuccess =
  updateChannelDirectResponse200 & {
    headers: Headers;
  };
export type updateChannelDirectResponseError = (
  | updateChannelDirectResponse400
  | updateChannelDirectResponse401
  | updateChannelDirectResponse403
  | updateChannelDirectResponse404
  | updateChannelDirectResponse422
  | updateChannelDirectResponse429
  | updateChannelDirectResponse500
) & {
  headers: Headers;
};

export type updateChannelDirectResponse =
  | updateChannelDirectResponseSuccess
  | updateChannelDirectResponseError;

export const getUpdateChannelDirectUrl = (id: string) => {
  return `/api/v2/channels/${id}`;
};

/**
 * Updates an Channel
 * @summary Updates an Channel
 */
export const updateChannelDirect = async (
  id: string,
  channelDirect: ChannelDirect,
  options?: RequestInit,
): Promise<updateChannelDirectResponse> => {
  return restFetch<updateChannelDirectResponse>(getUpdateChannelDirectUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(channelDirect),
  });
};

export const getUpdateChannelDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateChannelDirect>>,
    TError,
    { id: string; data: ChannelDirect },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateChannelDirect>>,
  TError,
  { id: string; data: ChannelDirect },
  TContext
> => {
  const mutationKey = ["updateChannelDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateChannelDirect>>,
    { id: string; data: ChannelDirect }
  > = props => {
    const { id, data } = props ?? {};

    return updateChannelDirect(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateChannelDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateChannelDirect>>
>;
export type UpdateChannelDirectMutationBody = ChannelDirect;
export type UpdateChannelDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Updates an Channel
 */
export const useUpdateChannelDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateChannelDirect>>,
      TError,
      { id: string; data: ChannelDirect },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateChannelDirect>>,
  TError,
  { id: string; data: ChannelDirect },
  TContext
> => {
  return useMutation(
    getUpdateChannelDirectMutationOptions(options),
    queryClient,
  );
};
export type searchChannelsDirectResponse200 = {
  data: ChannelList;
  status: 200;
};

export type searchChannelsDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type searchChannelsDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type searchChannelsDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type searchChannelsDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type searchChannelsDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type searchChannelsDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type searchChannelsDirectResponseSuccess =
  searchChannelsDirectResponse200 & {
    headers: Headers;
  };
export type searchChannelsDirectResponseError = (
  | searchChannelsDirectResponse400
  | searchChannelsDirectResponse401
  | searchChannelsDirectResponse403
  | searchChannelsDirectResponse422
  | searchChannelsDirectResponse429
  | searchChannelsDirectResponse500
) & {
  headers: Headers;
};

export type searchChannelsDirectResponse =
  | searchChannelsDirectResponseSuccess
  | searchChannelsDirectResponseError;

export const getSearchChannelsDirectUrl = () => {
  return `/api/v2/channels/search`;
};

/**
 * **Deprecated.** Use `GET /channels` with query parameters instead.
 *
 * Search for Channels.
 * @deprecated
 * @summary Search for Channels (deprecated)
 */
export const searchChannelsDirect = async (
  channelDirect: ChannelDirect,
  options?: RequestInit,
): Promise<searchChannelsDirectResponse> => {
  return restFetch<searchChannelsDirectResponse>(getSearchChannelsDirectUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(channelDirect),
  });
};

export const getSearchChannelsDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchChannelsDirect>>,
    TError,
    { data: ChannelDirect },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchChannelsDirect>>,
  TError,
  { data: ChannelDirect },
  TContext
> => {
  const mutationKey = ["searchChannelsDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchChannelsDirect>>,
    { data: ChannelDirect }
  > = props => {
    const { data } = props ?? {};

    return searchChannelsDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SearchChannelsDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchChannelsDirect>>
>;
export type SearchChannelsDirectMutationBody = ChannelDirect;
export type SearchChannelsDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @deprecated
 * @summary Search for Channels (deprecated)
 */
export const useSearchChannelsDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof searchChannelsDirect>>,
      TError,
      { data: ChannelDirect },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof searchChannelsDirect>>,
  TError,
  { data: ChannelDirect },
  TContext
> => {
  return useMutation(
    getSearchChannelsDirectMutationOptions(options),
    queryClient,
  );
};
export type listLedgerConfigurationsDirectResponse200 = {
  data: LedgerConfigurationList;
  status: 200;
};

export type listLedgerConfigurationsDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type listLedgerConfigurationsDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type listLedgerConfigurationsDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type listLedgerConfigurationsDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type listLedgerConfigurationsDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type listLedgerConfigurationsDirectResponseSuccess =
  listLedgerConfigurationsDirectResponse200 & {
    headers: Headers;
  };
export type listLedgerConfigurationsDirectResponseError = (
  | listLedgerConfigurationsDirectResponse400
  | listLedgerConfigurationsDirectResponse401
  | listLedgerConfigurationsDirectResponse403
  | listLedgerConfigurationsDirectResponse429
  | listLedgerConfigurationsDirectResponse500
) & {
  headers: Headers;
};

export type listLedgerConfigurationsDirectResponse =
  | listLedgerConfigurationsDirectResponseSuccess
  | listLedgerConfigurationsDirectResponseError;

export const getListLedgerConfigurationsDirectUrl = (
  params?: ListLedgerConfigurationsDirectParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v2/ledger-configurations?${stringifiedParams}`
    : `/api/v2/ledger-configurations`;
};

/**
 * **Internal-facing endpoint** used to retrieve all or a filtered set of ledger configurations
 * on the Thredd platform.
 *
 * This endpoint provides filtering capabilities for retrieving ledger configuration records.
 * You can use one or more query parameters to filter the results.
 *
 * ## Dependencies
 *
 * None.
 *
 * ## Filtering Behaviour
 *
 * - **No query parameters**: Returns all accessible ledger configurations
 * - **Single query parameter**: Filters configurations matching that specific field value
 * - **Multiple query parameters**: Returns configurations matching ALL specified criteria (AND logic)
 *
 * ## Searchable Fields
 *
 * The following query parameters can be used as filters:
 * - `client_id` - Filter by owner client UUID
 * - `financial_product_id` - Filter by associated financial product UUID
 * - `name` - Filter by ledger configuration name (supports partial matching)
 * - `currency` - Filter by currency code (ISO 4217, e.g. GBP, USD, EUR)
 *
 * ## Use Cases
 *
 * - List all ledger configurations for a specific client
 * - Find ledger configurations linked to a specific financial product
 * - Filter configurations by currency
 * - Retrieve configurations matching specific naming criteria
 * @summary Search and list ledger configurations
 */
export const listLedgerConfigurationsDirect = async (
  params?: ListLedgerConfigurationsDirectParams,
  options?: RequestInit,
): Promise<listLedgerConfigurationsDirectResponse> => {
  return restFetch<listLedgerConfigurationsDirectResponse>(
    getListLedgerConfigurationsDirectUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListLedgerConfigurationsDirectQueryKey = (
  params?: ListLedgerConfigurationsDirectParams,
) => {
  return [
    `/api/v2/ledger-configurations`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListLedgerConfigurationsDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListLedgerConfigurationsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListLedgerConfigurationsDirectQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>
  > = ({ signal }) =>
    listLedgerConfigurationsDirect(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListLedgerConfigurationsDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>
>;
export type ListLedgerConfigurationsDirectQueryError =
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useListLedgerConfigurationsDirect<
  TData = Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params: undefined | ListLedgerConfigurationsDirectParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
          TError,
          Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListLedgerConfigurationsDirect<
  TData = Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListLedgerConfigurationsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
          TError,
          Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListLedgerConfigurationsDirect<
  TData = Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListLedgerConfigurationsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Search and list ledger configurations
 */

export function useListLedgerConfigurationsDirect<
  TData = Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListLedgerConfigurationsDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listLedgerConfigurationsDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListLedgerConfigurationsDirectQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type createLedgerConfigurationDirectResponse201 = {
  data: LedgerConfigurationDirect;
  status: 201;
};

export type createLedgerConfigurationDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type createLedgerConfigurationDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type createLedgerConfigurationDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type createLedgerConfigurationDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type createLedgerConfigurationDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type createLedgerConfigurationDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type createLedgerConfigurationDirectResponseSuccess =
  createLedgerConfigurationDirectResponse201 & {
    headers: Headers;
  };
export type createLedgerConfigurationDirectResponseError = (
  | createLedgerConfigurationDirectResponse400
  | createLedgerConfigurationDirectResponse401
  | createLedgerConfigurationDirectResponse403
  | createLedgerConfigurationDirectResponse422
  | createLedgerConfigurationDirectResponse429
  | createLedgerConfigurationDirectResponse500
) & {
  headers: Headers;
};

export type createLedgerConfigurationDirectResponse =
  | createLedgerConfigurationDirectResponseSuccess
  | createLedgerConfigurationDirectResponseError;

export const getCreateLedgerConfigurationDirectUrl = () => {
  return `/api/v2/ledger-configurations`;
};

/**
 * Creates a Ledger Configuration
 * @summary Creates a Ledger Configuration
 */
export const createLedgerConfigurationDirect = async (
  ledgerConfigurationDirect: LedgerConfigurationDirect,
  options?: RequestInit,
): Promise<createLedgerConfigurationDirectResponse> => {
  return restFetch<createLedgerConfigurationDirectResponse>(
    getCreateLedgerConfigurationDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(ledgerConfigurationDirect),
    },
  );
};

export const getCreateLedgerConfigurationDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createLedgerConfigurationDirect>>,
    TError,
    { data: LedgerConfigurationDirect },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createLedgerConfigurationDirect>>,
  TError,
  { data: LedgerConfigurationDirect },
  TContext
> => {
  const mutationKey = ["createLedgerConfigurationDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createLedgerConfigurationDirect>>,
    { data: LedgerConfigurationDirect }
  > = props => {
    const { data } = props ?? {};

    return createLedgerConfigurationDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateLedgerConfigurationDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof createLedgerConfigurationDirect>>
>;
export type CreateLedgerConfigurationDirectMutationBody =
  LedgerConfigurationDirect;
export type CreateLedgerConfigurationDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Creates a Ledger Configuration
 */
export const useCreateLedgerConfigurationDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createLedgerConfigurationDirect>>,
      TError,
      { data: LedgerConfigurationDirect },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createLedgerConfigurationDirect>>,
  TError,
  { data: LedgerConfigurationDirect },
  TContext
> => {
  return useMutation(
    getCreateLedgerConfigurationDirectMutationOptions(options),
    queryClient,
  );
};
export type getLedgerConfigurationDirectResponse200 = {
  data: LedgerConfigurationDirect;
  status: 200;
};

export type getLedgerConfigurationDirectResponse400 = {
  data: RequestBadRequestResponse;
  status: 400;
};

export type getLedgerConfigurationDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type getLedgerConfigurationDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type getLedgerConfigurationDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type getLedgerConfigurationDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type getLedgerConfigurationDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type getLedgerConfigurationDirectResponseSuccess =
  getLedgerConfigurationDirectResponse200 & {
    headers: Headers;
  };
export type getLedgerConfigurationDirectResponseError = (
  | getLedgerConfigurationDirectResponse400
  | getLedgerConfigurationDirectResponse401
  | getLedgerConfigurationDirectResponse403
  | getLedgerConfigurationDirectResponse404
  | getLedgerConfigurationDirectResponse429
  | getLedgerConfigurationDirectResponse500
) & {
  headers: Headers;
};

export type getLedgerConfigurationDirectResponse =
  | getLedgerConfigurationDirectResponseSuccess
  | getLedgerConfigurationDirectResponseError;

export const getGetLedgerConfigurationDirectUrl = (id: string) => {
  return `/api/v2/ledger-configurations/${id}`;
};

/**
 * Retrieves a single Ledger Configuration including its associated instrument product IDs.
 * @summary Retrieves a Ledger Configuration by ID
 */
export const getLedgerConfigurationDirect = async (
  id: string,
  options?: RequestInit,
): Promise<getLedgerConfigurationDirectResponse> => {
  return restFetch<getLedgerConfigurationDirectResponse>(
    getGetLedgerConfigurationDirectUrl(id),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetLedgerConfigurationDirectQueryKey = (id: string) => {
  return [`/api/v2/ledger-configurations/${id}`] as const;
};

export const getGetLedgerConfigurationDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetLedgerConfigurationDirectQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getLedgerConfigurationDirect>>
  > = ({ signal }) =>
    getLedgerConfigurationDirect(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLedgerConfigurationDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLedgerConfigurationDirect>>
>;
export type GetLedgerConfigurationDirectQueryError =
  | RequestBadRequestResponse
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useGetLedgerConfigurationDirect<
  TData = Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
          TError,
          Awaited<ReturnType<typeof getLedgerConfigurationDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLedgerConfigurationDirect<
  TData = Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
          TError,
          Awaited<ReturnType<typeof getLedgerConfigurationDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLedgerConfigurationDirect<
  TData = Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Retrieves a Ledger Configuration by ID
 */

export function useGetLedgerConfigurationDirect<
  TData = Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
  TError =
    | RequestBadRequestResponse
    | ProblemDetails
    | AuthForbiddenResponse
    | RateLimitExceededResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLedgerConfigurationDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetLedgerConfigurationDirectQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updateLedgerConfigurationDirectResponse200 = {
  data: LedgerConfigurationDirect;
  status: 200;
};

export type updateLedgerConfigurationDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type updateLedgerConfigurationDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type updateLedgerConfigurationDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type updateLedgerConfigurationDirectResponse404 = {
  data: ProblemDetails;
  status: 404;
};

export type updateLedgerConfigurationDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type updateLedgerConfigurationDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type updateLedgerConfigurationDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type updateLedgerConfigurationDirectResponseSuccess =
  updateLedgerConfigurationDirectResponse200 & {
    headers: Headers;
  };
export type updateLedgerConfigurationDirectResponseError = (
  | updateLedgerConfigurationDirectResponse400
  | updateLedgerConfigurationDirectResponse401
  | updateLedgerConfigurationDirectResponse403
  | updateLedgerConfigurationDirectResponse404
  | updateLedgerConfigurationDirectResponse422
  | updateLedgerConfigurationDirectResponse429
  | updateLedgerConfigurationDirectResponse500
) & {
  headers: Headers;
};

export type updateLedgerConfigurationDirectResponse =
  | updateLedgerConfigurationDirectResponseSuccess
  | updateLedgerConfigurationDirectResponseError;

export const getUpdateLedgerConfigurationDirectUrl = (id: string) => {
  return `/api/v2/ledger-configurations/${id}`;
};

/**
 * Updates a Ledger Configuration
 * @summary Updates a Ledger Configuration
 */
export const updateLedgerConfigurationDirect = async (
  id: string,
  ledgerConfigurationDirect: LedgerConfigurationDirect,
  options?: RequestInit,
): Promise<updateLedgerConfigurationDirectResponse> => {
  return restFetch<updateLedgerConfigurationDirectResponse>(
    getUpdateLedgerConfigurationDirectUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(ledgerConfigurationDirect),
    },
  );
};

export const getUpdateLedgerConfigurationDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateLedgerConfigurationDirect>>,
    TError,
    { id: string; data: LedgerConfigurationDirect },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateLedgerConfigurationDirect>>,
  TError,
  { id: string; data: LedgerConfigurationDirect },
  TContext
> => {
  const mutationKey = ["updateLedgerConfigurationDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateLedgerConfigurationDirect>>,
    { id: string; data: LedgerConfigurationDirect }
  > = props => {
    const { id, data } = props ?? {};

    return updateLedgerConfigurationDirect(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateLedgerConfigurationDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateLedgerConfigurationDirect>>
>;
export type UpdateLedgerConfigurationDirectMutationBody =
  LedgerConfigurationDirect;
export type UpdateLedgerConfigurationDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Updates a Ledger Configuration
 */
export const useUpdateLedgerConfigurationDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateLedgerConfigurationDirect>>,
      TError,
      { id: string; data: LedgerConfigurationDirect },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateLedgerConfigurationDirect>>,
  TError,
  { id: string; data: LedgerConfigurationDirect },
  TContext
> => {
  return useMutation(
    getUpdateLedgerConfigurationDirectMutationOptions(options),
    queryClient,
  );
};
export type searchLedgerConfigurationsDirectResponse200 = {
  data: LedgerConfigurationList;
  status: 200;
};

export type searchLedgerConfigurationsDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type searchLedgerConfigurationsDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type searchLedgerConfigurationsDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type searchLedgerConfigurationsDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type searchLedgerConfigurationsDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type searchLedgerConfigurationsDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type searchLedgerConfigurationsDirectResponseSuccess =
  searchLedgerConfigurationsDirectResponse200 & {
    headers: Headers;
  };
export type searchLedgerConfigurationsDirectResponseError = (
  | searchLedgerConfigurationsDirectResponse400
  | searchLedgerConfigurationsDirectResponse401
  | searchLedgerConfigurationsDirectResponse403
  | searchLedgerConfigurationsDirectResponse422
  | searchLedgerConfigurationsDirectResponse429
  | searchLedgerConfigurationsDirectResponse500
) & {
  headers: Headers;
};

export type searchLedgerConfigurationsDirectResponse =
  | searchLedgerConfigurationsDirectResponseSuccess
  | searchLedgerConfigurationsDirectResponseError;

export const getSearchLedgerConfigurationsDirectUrl = () => {
  return `/api/v2/ledger-configurations/search`;
};

/**
 * **Deprecated.** Use `GET /ledger-configurations` with query parameters instead.
 *
 * Searches for Ledger Configurations.
 * @deprecated
 * @summary Search Ledger Configurations (deprecated)
 */
export const searchLedgerConfigurationsDirect = async (
  ledgerConfigurationSummary: LedgerConfigurationSummary,
  options?: RequestInit,
): Promise<searchLedgerConfigurationsDirectResponse> => {
  return restFetch<searchLedgerConfigurationsDirectResponse>(
    getSearchLedgerConfigurationsDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(ledgerConfigurationSummary),
    },
  );
};

export const getSearchLedgerConfigurationsDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchLedgerConfigurationsDirect>>,
    TError,
    { data: LedgerConfigurationSummary },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchLedgerConfigurationsDirect>>,
  TError,
  { data: LedgerConfigurationSummary },
  TContext
> => {
  const mutationKey = ["searchLedgerConfigurationsDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchLedgerConfigurationsDirect>>,
    { data: LedgerConfigurationSummary }
  > = props => {
    const { data } = props ?? {};

    return searchLedgerConfigurationsDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SearchLedgerConfigurationsDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchLedgerConfigurationsDirect>>
>;
export type SearchLedgerConfigurationsDirectMutationBody =
  LedgerConfigurationSummary;
export type SearchLedgerConfigurationsDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @deprecated
 * @summary Search Ledger Configurations (deprecated)
 */
export const useSearchLedgerConfigurationsDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof searchLedgerConfigurationsDirect>>,
      TError,
      { data: LedgerConfigurationSummary },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof searchLedgerConfigurationsDirect>>,
  TError,
  { data: LedgerConfigurationSummary },
  TContext
> => {
  return useMutation(
    getSearchLedgerConfigurationsDirectMutationOptions(options),
    queryClient,
  );
};
export type listOffsetLedgersDirectResponse200 = {
  data: OffsetLedgerList;
  status: 200;
};

export type listOffsetLedgersDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type listOffsetLedgersDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type listOffsetLedgersDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type listOffsetLedgersDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type listOffsetLedgersDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type listOffsetLedgersDirectResponseSuccess =
  listOffsetLedgersDirectResponse200 & {
    headers: Headers;
  };
export type listOffsetLedgersDirectResponseError = (
  | listOffsetLedgersDirectResponse400
  | listOffsetLedgersDirectResponse401
  | listOffsetLedgersDirectResponse403
  | listOffsetLedgersDirectResponse429
  | listOffsetLedgersDirectResponse500
) & {
  headers: Headers;
};

export type listOffsetLedgersDirectResponse =
  | listOffsetLedgersDirectResponseSuccess
  | listOffsetLedgersDirectResponseError;

export const getListOffsetLedgersDirectUrl = (
  params?: ListOffsetLedgersDirectParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v2/offset-ledgers?${stringifiedParams}`
    : `/api/v2/offset-ledgers`;
};

/**
 * **Internal-facing endpoint** used to retrieve all or a filtered set of offset ledgers
 * on the Thredd platform.
 *
 * This endpoint provides filtering capabilities for retrieving offset ledger records.
 * You can use one or more query parameters to filter the results.
 *
 * ## Dependencies
 *
 * None.
 *
 * ## Filtering Behaviour
 *
 * - **No query parameters**: Returns all accessible offset ledgers
 * - **Single query parameter**: Filters offset ledgers matching that specific field value
 * - **Multiple query parameters**: Returns offset ledgers matching ALL specified criteria (AND logic)
 *
 * ## Searchable Fields
 *
 * The following query parameters can be used as filters:
 * - `client_id` - Filter by owner client UUID
 * - `channel_id` - Filter by associated channel UUID
 * - `currency` - Filter by currency code (ISO 4217, e.g. GBP, USD, EUR)
 * - `offset_ledger_type` - Filter by offset ledger type (e.g. CLIENT_FEES)
 *
 * ## Use Cases
 *
 * - List all offset ledgers for a specific client
 * - Find offset ledgers linked to a specific channel
 * - Filter offset ledgers by currency
 * - Retrieve offset ledgers of a specific type
 * @summary Search and list offset ledgers
 */
export const listOffsetLedgersDirect = async (
  params?: ListOffsetLedgersDirectParams,
  options?: RequestInit,
): Promise<listOffsetLedgersDirectResponse> => {
  return restFetch<listOffsetLedgersDirectResponse>(
    getListOffsetLedgersDirectUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListOffsetLedgersDirectQueryKey = (
  params?: ListOffsetLedgersDirectParams,
) => {
  return [`/api/v2/offset-ledgers`, ...(params ? [params] : [])] as const;
};

export const getListOffsetLedgersDirectQueryOptions = <
  TData = Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListOffsetLedgersDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListOffsetLedgersDirectQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listOffsetLedgersDirect>>
  > = ({ signal }) =>
    listOffsetLedgersDirect(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListOffsetLedgersDirectQueryResult = NonNullable<
  Awaited<ReturnType<typeof listOffsetLedgersDirect>>
>;
export type ListOffsetLedgersDirectQueryError =
  | ProblemDetails
  | AuthForbiddenResponse
  | RateLimitExceededResponse;

export function useListOffsetLedgersDirect<
  TData = Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params: undefined | ListOffsetLedgersDirectParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
          TError,
          Awaited<ReturnType<typeof listOffsetLedgersDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOffsetLedgersDirect<
  TData = Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListOffsetLedgersDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
          TError,
          Awaited<ReturnType<typeof listOffsetLedgersDirect>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOffsetLedgersDirect<
  TData = Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListOffsetLedgersDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Search and list offset ledgers
 */

export function useListOffsetLedgersDirect<
  TData = Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
  TError = ProblemDetails | AuthForbiddenResponse | RateLimitExceededResponse,
>(
  params?: ListOffsetLedgersDirectParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOffsetLedgersDirect>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListOffsetLedgersDirectQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type searchOffsetLedgersDirectResponse200 = {
  data: OffsetLedgerList;
  status: 200;
};

export type searchOffsetLedgersDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type searchOffsetLedgersDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type searchOffsetLedgersDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type searchOffsetLedgersDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type searchOffsetLedgersDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type searchOffsetLedgersDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type searchOffsetLedgersDirectResponseSuccess =
  searchOffsetLedgersDirectResponse200 & {
    headers: Headers;
  };
export type searchOffsetLedgersDirectResponseError = (
  | searchOffsetLedgersDirectResponse400
  | searchOffsetLedgersDirectResponse401
  | searchOffsetLedgersDirectResponse403
  | searchOffsetLedgersDirectResponse422
  | searchOffsetLedgersDirectResponse429
  | searchOffsetLedgersDirectResponse500
) & {
  headers: Headers;
};

export type searchOffsetLedgersDirectResponse =
  | searchOffsetLedgersDirectResponseSuccess
  | searchOffsetLedgersDirectResponseError;

export const getSearchOffsetLedgersDirectUrl = () => {
  return `/api/v2/offset-ledgers/search`;
};

/**
 * **Deprecated.** Use `GET /offset-ledgers` with query parameters instead.
 *
 * Searches for OffsetLedgers.
 * @deprecated
 * @summary Search OffsetLedgers (deprecated)
 */
export const searchOffsetLedgersDirect = async (
  offsetLedger: NonReadonly<OffsetLedger>,
  options?: RequestInit,
): Promise<searchOffsetLedgersDirectResponse> => {
  return restFetch<searchOffsetLedgersDirectResponse>(
    getSearchOffsetLedgersDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(offsetLedger),
    },
  );
};

export const getSearchOffsetLedgersDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchOffsetLedgersDirect>>,
    TError,
    { data: NonReadonly<OffsetLedger> },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchOffsetLedgersDirect>>,
  TError,
  { data: NonReadonly<OffsetLedger> },
  TContext
> => {
  const mutationKey = ["searchOffsetLedgersDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchOffsetLedgersDirect>>,
    { data: NonReadonly<OffsetLedger> }
  > = props => {
    const { data } = props ?? {};

    return searchOffsetLedgersDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SearchOffsetLedgersDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchOffsetLedgersDirect>>
>;
export type SearchOffsetLedgersDirectMutationBody = NonReadonly<OffsetLedger>;
export type SearchOffsetLedgersDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @deprecated
 * @summary Search OffsetLedgers (deprecated)
 */
export const useSearchOffsetLedgersDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof searchOffsetLedgersDirect>>,
      TError,
      { data: NonReadonly<OffsetLedger> },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof searchOffsetLedgersDirect>>,
  TError,
  { data: NonReadonly<OffsetLedger> },
  TContext
> => {
  return useMutation(
    getSearchOffsetLedgersDirectMutationOptions(options),
    queryClient,
  );
};
export type createCountryCurrencyCodeDirectResponse201 = {
  data: CountryCurrencyCode;
  status: 201;
};

export type createCountryCurrencyCodeDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type createCountryCurrencyCodeDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type createCountryCurrencyCodeDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type createCountryCurrencyCodeDirectResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type createCountryCurrencyCodeDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type createCountryCurrencyCodeDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type createCountryCurrencyCodeDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type createCountryCurrencyCodeDirectResponseSuccess =
  createCountryCurrencyCodeDirectResponse201 & {
    headers: Headers;
  };
export type createCountryCurrencyCodeDirectResponseError = (
  | createCountryCurrencyCodeDirectResponse400
  | createCountryCurrencyCodeDirectResponse401
  | createCountryCurrencyCodeDirectResponse403
  | createCountryCurrencyCodeDirectResponse409
  | createCountryCurrencyCodeDirectResponse422
  | createCountryCurrencyCodeDirectResponse429
  | createCountryCurrencyCodeDirectResponse500
) & {
  headers: Headers;
};

export type createCountryCurrencyCodeDirectResponse =
  | createCountryCurrencyCodeDirectResponseSuccess
  | createCountryCurrencyCodeDirectResponseError;

export const getCreateCountryCurrencyCodeDirectUrl = () => {
  return `/api/v2/reference-data/currencies`;
};

/**
 * Creates a Country Currency Code.
 * @summary Creates a Country Currency Code.
 */
export const createCountryCurrencyCodeDirect = async (
  countryCurrencyCode: CountryCurrencyCode,
  options?: RequestInit,
): Promise<createCountryCurrencyCodeDirectResponse> => {
  return restFetch<createCountryCurrencyCodeDirectResponse>(
    getCreateCountryCurrencyCodeDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(countryCurrencyCode),
    },
  );
};

export const getCreateCountryCurrencyCodeDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCountryCurrencyCodeDirect>>,
    TError,
    { data: CountryCurrencyCode },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createCountryCurrencyCodeDirect>>,
  TError,
  { data: CountryCurrencyCode },
  TContext
> => {
  const mutationKey = ["createCountryCurrencyCodeDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createCountryCurrencyCodeDirect>>,
    { data: CountryCurrencyCode }
  > = props => {
    const { data } = props ?? {};

    return createCountryCurrencyCodeDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCountryCurrencyCodeDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof createCountryCurrencyCodeDirect>>
>;
export type CreateCountryCurrencyCodeDirectMutationBody = CountryCurrencyCode;
export type CreateCountryCurrencyCodeDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Creates a Country Currency Code.
 */
export const useCreateCountryCurrencyCodeDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createCountryCurrencyCodeDirect>>,
      TError,
      { data: CountryCurrencyCode },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createCountryCurrencyCodeDirect>>,
  TError,
  { data: CountryCurrencyCode },
  TContext
> => {
  return useMutation(
    getCreateCountryCurrencyCodeDirectMutationOptions(options),
    queryClient,
  );
};
export type searchCountryCurrencyCodeDirectResponse200 = {
  data: CountryCurrencyCodeList;
  status: 200;
};

export type searchCountryCurrencyCodeDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type searchCountryCurrencyCodeDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type searchCountryCurrencyCodeDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type searchCountryCurrencyCodeDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type searchCountryCurrencyCodeDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type searchCountryCurrencyCodeDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type searchCountryCurrencyCodeDirectResponseSuccess =
  searchCountryCurrencyCodeDirectResponse200 & {
    headers: Headers;
  };
export type searchCountryCurrencyCodeDirectResponseError = (
  | searchCountryCurrencyCodeDirectResponse400
  | searchCountryCurrencyCodeDirectResponse401
  | searchCountryCurrencyCodeDirectResponse403
  | searchCountryCurrencyCodeDirectResponse422
  | searchCountryCurrencyCodeDirectResponse429
  | searchCountryCurrencyCodeDirectResponse500
) & {
  headers: Headers;
};

export type searchCountryCurrencyCodeDirectResponse =
  | searchCountryCurrencyCodeDirectResponseSuccess
  | searchCountryCurrencyCodeDirectResponseError;

export const getSearchCountryCurrencyCodeDirectUrl = () => {
  return `/api/v2/reference-data/currencies/search`;
};

/**
 * Search for Country Currency Codes.
 * @summary Search for Country Currency Codes.
 */
export const searchCountryCurrencyCodeDirect = async (
  countryCurrencyCode: CountryCurrencyCode,
  options?: RequestInit,
): Promise<searchCountryCurrencyCodeDirectResponse> => {
  return restFetch<searchCountryCurrencyCodeDirectResponse>(
    getSearchCountryCurrencyCodeDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(countryCurrencyCode),
    },
  );
};

export const getSearchCountryCurrencyCodeDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchCountryCurrencyCodeDirect>>,
    TError,
    { data: CountryCurrencyCode },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchCountryCurrencyCodeDirect>>,
  TError,
  { data: CountryCurrencyCode },
  TContext
> => {
  const mutationKey = ["searchCountryCurrencyCodeDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchCountryCurrencyCodeDirect>>,
    { data: CountryCurrencyCode }
  > = props => {
    const { data } = props ?? {};

    return searchCountryCurrencyCodeDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SearchCountryCurrencyCodeDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchCountryCurrencyCodeDirect>>
>;
export type SearchCountryCurrencyCodeDirectMutationBody = CountryCurrencyCode;
export type SearchCountryCurrencyCodeDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Search for Country Currency Codes.
 */
export const useSearchCountryCurrencyCodeDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof searchCountryCurrencyCodeDirect>>,
      TError,
      { data: CountryCurrencyCode },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof searchCountryCurrencyCodeDirect>>,
  TError,
  { data: CountryCurrencyCode },
  TContext
> => {
  return useMutation(
    getSearchCountryCurrencyCodeDirectMutationOptions(options),
    queryClient,
  );
};
export type createMerchantCategoryCodeDirectResponse201 = {
  data: MerchantCategoryCode;
  status: 201;
};

export type createMerchantCategoryCodeDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type createMerchantCategoryCodeDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type createMerchantCategoryCodeDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type createMerchantCategoryCodeDirectResponse409 = {
  data: ProblemDetails;
  status: 409;
};

export type createMerchantCategoryCodeDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type createMerchantCategoryCodeDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type createMerchantCategoryCodeDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type createMerchantCategoryCodeDirectResponseSuccess =
  createMerchantCategoryCodeDirectResponse201 & {
    headers: Headers;
  };
export type createMerchantCategoryCodeDirectResponseError = (
  | createMerchantCategoryCodeDirectResponse400
  | createMerchantCategoryCodeDirectResponse401
  | createMerchantCategoryCodeDirectResponse403
  | createMerchantCategoryCodeDirectResponse409
  | createMerchantCategoryCodeDirectResponse422
  | createMerchantCategoryCodeDirectResponse429
  | createMerchantCategoryCodeDirectResponse500
) & {
  headers: Headers;
};

export type createMerchantCategoryCodeDirectResponse =
  | createMerchantCategoryCodeDirectResponseSuccess
  | createMerchantCategoryCodeDirectResponseError;

export const getCreateMerchantCategoryCodeDirectUrl = () => {
  return `/api/v2/reference-data/mcc`;
};

/**
 * Creates a Merchant Category Code.
 * @summary Creates a Merchant Category Code.
 */
export const createMerchantCategoryCodeDirect = async (
  merchantCategoryCode: MerchantCategoryCode,
  options?: RequestInit,
): Promise<createMerchantCategoryCodeDirectResponse> => {
  return restFetch<createMerchantCategoryCodeDirectResponse>(
    getCreateMerchantCategoryCodeDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(merchantCategoryCode),
    },
  );
};

export const getCreateMerchantCategoryCodeDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMerchantCategoryCodeDirect>>,
    TError,
    { data: MerchantCategoryCode },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMerchantCategoryCodeDirect>>,
  TError,
  { data: MerchantCategoryCode },
  TContext
> => {
  const mutationKey = ["createMerchantCategoryCodeDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMerchantCategoryCodeDirect>>,
    { data: MerchantCategoryCode }
  > = props => {
    const { data } = props ?? {};

    return createMerchantCategoryCodeDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMerchantCategoryCodeDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMerchantCategoryCodeDirect>>
>;
export type CreateMerchantCategoryCodeDirectMutationBody = MerchantCategoryCode;
export type CreateMerchantCategoryCodeDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Creates a Merchant Category Code.
 */
export const useCreateMerchantCategoryCodeDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createMerchantCategoryCodeDirect>>,
      TError,
      { data: MerchantCategoryCode },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createMerchantCategoryCodeDirect>>,
  TError,
  { data: MerchantCategoryCode },
  TContext
> => {
  return useMutation(
    getCreateMerchantCategoryCodeDirectMutationOptions(options),
    queryClient,
  );
};
export type searchMerchantCategoryCodeDirectResponse200 = {
  data: MerchantCategoryCodeList;
  status: 200;
};

export type searchMerchantCategoryCodeDirectResponse400 = {
  data: ProblemDetails;
  status: 400;
};

export type searchMerchantCategoryCodeDirectResponse401 = {
  data: ProblemDetails;
  status: 401;
};

export type searchMerchantCategoryCodeDirectResponse403 = {
  data: AuthForbiddenResponse;
  status: 403;
};

export type searchMerchantCategoryCodeDirectResponse422 = {
  data: ValidationInvalidResponse;
  status: 422;
};

export type searchMerchantCategoryCodeDirectResponse429 = {
  data: RateLimitExceededResponse;
  status: 429;
};

export type searchMerchantCategoryCodeDirectResponse500 = {
  data: ProblemDetails;
  status: 500;
};

export type searchMerchantCategoryCodeDirectResponseSuccess =
  searchMerchantCategoryCodeDirectResponse200 & {
    headers: Headers;
  };
export type searchMerchantCategoryCodeDirectResponseError = (
  | searchMerchantCategoryCodeDirectResponse400
  | searchMerchantCategoryCodeDirectResponse401
  | searchMerchantCategoryCodeDirectResponse403
  | searchMerchantCategoryCodeDirectResponse422
  | searchMerchantCategoryCodeDirectResponse429
  | searchMerchantCategoryCodeDirectResponse500
) & {
  headers: Headers;
};

export type searchMerchantCategoryCodeDirectResponse =
  | searchMerchantCategoryCodeDirectResponseSuccess
  | searchMerchantCategoryCodeDirectResponseError;

export const getSearchMerchantCategoryCodeDirectUrl = () => {
  return `/api/v2/reference-data/mcc/search`;
};

/**
 * Search for MerchantmCategory Codes.
 * @summary Search for Merchant Category Codes.
 */
export const searchMerchantCategoryCodeDirect = async (
  merchantCategoryCode: MerchantCategoryCode,
  options?: RequestInit,
): Promise<searchMerchantCategoryCodeDirectResponse> => {
  return restFetch<searchMerchantCategoryCodeDirectResponse>(
    getSearchMerchantCategoryCodeDirectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(merchantCategoryCode),
    },
  );
};

export const getSearchMerchantCategoryCodeDirectMutationOptions = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchMerchantCategoryCodeDirect>>,
    TError,
    { data: MerchantCategoryCode },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchMerchantCategoryCodeDirect>>,
  TError,
  { data: MerchantCategoryCode },
  TContext
> => {
  const mutationKey = ["searchMerchantCategoryCodeDirect"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchMerchantCategoryCodeDirect>>,
    { data: MerchantCategoryCode }
  > = props => {
    const { data } = props ?? {};

    return searchMerchantCategoryCodeDirect(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SearchMerchantCategoryCodeDirectMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchMerchantCategoryCodeDirect>>
>;
export type SearchMerchantCategoryCodeDirectMutationBody = MerchantCategoryCode;
export type SearchMerchantCategoryCodeDirectMutationError =
  | ProblemDetails
  | AuthForbiddenResponse
  | ValidationInvalidResponse
  | RateLimitExceededResponse;

/**
 * @summary Search for Merchant Category Codes.
 */
export const useSearchMerchantCategoryCodeDirect = <
  TError =
    | ProblemDetails
    | AuthForbiddenResponse
    | ValidationInvalidResponse
    | RateLimitExceededResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof searchMerchantCategoryCodeDirect>>,
      TError,
      { data: MerchantCategoryCode },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof searchMerchantCategoryCodeDirect>>,
  TError,
  { data: MerchantCategoryCode },
  TContext
> => {
  return useMutation(
    getSearchMerchantCategoryCodeDirectMutationOptions(options),
    queryClient,
  );
};
